// Live Practice — voice/text roleplay scenarios on notebook reader pages.
// See docs/live-practice-spec.md. Phase L1: interview MVP, BYOK-first, engine
// built transport-agnostic (voice + text) even though voice is the headline.
//
// The server renders only the inline card (pages/_block.html.erb). Everything
// here is behaviour: fetch the scenario brief, onboard a BYOK key, expand the
// card into a full-viewport stage, run the round against Gemini Live directly
// from the browser (Rails never sees the key or proxies audio), capture the
// transcript, and produce a short post-round review. GA4 events fire at every
// funnel cliff.
//
// Security posture (spec §Security): the key defaults to sessionStorage (dies
// with the tab), is sent ONLY to generativelanguage.googleapis.com, and is
// never attached to a Rails request, a GA event, or a log line.

const GEMINI_HOST = 'https://generativelanguage.googleapis.com'
const GEMINI_WS = 'wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent'
const KEY_NAME = 'ab_gemini_key'
const OUTPUT_RATE = 24000 // Gemini Live returns 24 kHz PCM

// ── small utilities ─────────────────────────────────────────────────────────

function gaEvent (name, params) {
  if (typeof window.gtag === 'function') window.gtag('event', name, params)
}

function readKey () {
  try { return localStorage.getItem(KEY_NAME) || sessionStorage.getItem(KEY_NAME) || null } catch (e) { return null }
}

// ── practice settings: learner name + practice language + round length ──────
// Non-sensitive (just a name, a language choice and a duration), so unlike
// the key there's no "remember on this device" choice: always in localStorage.
// duration 0 = "never picked": the scenario's authored length applies.
const PREFS_KEY = 'ab_live_prefs'

function readPrefs () {
  try { return Object.assign({ name: '', language: '', duration: 0 }, JSON.parse(localStorage.getItem(PREFS_KEY) || '{}')) } catch (e) { return { name: '', language: '', duration: 0 } }
}

function storePrefs (prefs) {
  try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)) } catch (e) { /* storage blocked — settings just won't persist */ }
}

// ── first-run onboarding flags ──────────────────────────────────────────────
// SETUP_KEY gates the one-time welcome step (firstRunSetup); STAGE_HINT_KEY
// gates the one-time in-stage coach mark pointing at the live goal counters
// (maybeShowStageHint). When storage is unreadable we report "already seen":
// better to skip onboarding for one rare visitor than to nag a
// storage-blocked browser on every single round.
const SETUP_KEY = 'ab_live_setup_done'
const STAGE_HINT_KEY = 'ab_live_stage_hint'

function flagSeen (key) {
  try { return localStorage.getItem(key) === '1' } catch (e) { return true }
}

function markFlag (key) {
  try { localStorage.setItem(key, '1') } catch (e) { /* storage blocked */ }
}

function storeKey (key, remember) {
  // Always write to one storage and clear the other: otherwise a key saved
  // with a different "remember" choice than last time leaves a stale value
  // sitting in the other storage, and readKey()'s localStorage-first lookup
  // keeps returning THAT one even though a new key was just saved.
  try {
    if (remember) { localStorage.setItem(KEY_NAME, key); sessionStorage.removeItem(KEY_NAME) } else { sessionStorage.setItem(KEY_NAME, key); localStorage.removeItem(KEY_NAME) }
  } catch (e) { /* storage blocked — key lives only in memory for this round */ }
}

function el (tag, cls, text) {
  const node = document.createElement(tag)
  if (cls) node.className = cls
  if (text != null) node.textContent = text
  return node
}

function base64FromBuffer (buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const CHUNK = 0x8000
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK))
  }
  return btoa(binary)
}

function int16FromBase64 (b64) {
  const binary = atob(b64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return new Int16Array(bytes.buffer)
}

// Loudness (RMS, 0..32768) of a PCM16 mic chunk. Used to tell a real
// interruption from the model's own playback echoed back by the mic.
function rmsFromBuffer (buffer) {
  const s = new Int16Array(buffer)
  if (!s.length) return 0
  let sum = 0
  for (let i = 0; i < s.length; i++) sum += s[i] * s[i]
  return Math.sqrt(sum / s.length)
}

// Below this the mic is hearing (echo-cancelled) room noise or leftover model
// audio, not the learner talking over the model. Above it = deliberate barge-in.
// Only applied WHILE the model is speaking; a silent model forwards everything.
const BARGE_IN_RMS = 1500

// Scrub anything that could echo the key (or a teaser ephemeral token, which
// is key-equivalent while it lives) out of an error before it's shown.
function safeError (err) {
  const msg = (err && err.message) ? err.message : String(err)
  return msg.replace(/key=[\w-]+/gi, 'key=***').replace(/auth_tokens\/[\w-]+/gi, 'auth_tokens/***')
}

function escHtml (s) {
  return String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
}

// getUserMedia rejects for several distinct reasons that all look identical to
// a learner unless we say which one: the in-page site prompt is only ONE gate.
// On macOS/Windows, the OS also gates mic access per-app (e.g. System Settings
// -> Privacy & Security -> Microphone -> the browser itself), separately from
// that prompt, so "I already allowed it" (the page permission) can still fail.
function micErrorMessage (e) {
  const name = e && e.name
  if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
    if (inAppBrowser()) {
      return 'This app\'s built-in browser is blocking the mic. Open this page in Safari or Chrome (look for "Open in browser" in the app\'s share menu) and try again.'
    }
    return "Your browser or OS is blocking the mic for this site. Check the site's mic permission (the icon in the address bar) AND your OS's microphone privacy settings for this browser, then reload the page."
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return 'No microphone was found. Check one is connected and selected as the input device, then retry.'
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return 'Your microphone is in use by another app or browser tab. Close it and retry.'
  }
  if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
    return "Your microphone doesn't support the required settings. Retry, or practise as text."
  }
  return 'We could not open your mic. You can practise as text instead.'
}

// In-app webviews (the Google app, Instagram, Facebook, LinkedIn, WeChat…)
// ship no getUserMedia at all on iOS: the mic API simply doesn't exist there,
// no permission prompt can fix it. Detected up-front so the learner gets
// "open this in your real browser" instead of a dead mic prompt.
function inAppBrowser () {
  return /\b(GSA|FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|MicroMessenger|Snapchat|Twitter)\b/i.test(navigator.userAgent)
}

function micSupported () {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
}

// ── model cascade: skip a model that failed (no preview access) or hit its ────
// daily RPD cap, so the next round starts on one that works and the skipped
// model is retried after ~12h (past the midnight-Pacific quota reset). Shared by
// the voice ladder (LIVE_MODELS) and the text/review ladder (REVIEW_MODELS).
const MODEL_SKIP_KEY = 'ab_live_model_skip'
const MODEL_SKIP_MS = 12 * 60 * 60 * 1000

function readModelSkip () {
  try { return JSON.parse(localStorage.getItem(MODEL_SKIP_KEY) || '{}') || {} } catch (e) { return {} }
}
function skipModel (name) {
  if (!name) return
  try {
    const s = readModelSkip()
    s[name] = Date.now() + MODEL_SKIP_MS
    localStorage.setItem(MODEL_SKIP_KEY, JSON.stringify(s))
  } catch (e) { /* storage blocked — just one round without the memo */ }
}
// The list with still-cooling models dropped, preserving order. If everything is
// cooling, return the original list (better to retry than have nothing to try).
function usableModels (list) {
  const arr = Array.isArray(list) ? list.slice() : []
  const s = readModelSkip()
  const now = Date.now()
  const live = arr.filter(m => !(s[m] && s[m] > now))
  return live.length ? live : arr
}

// ── round progression: capture, best-score badge, guest->member claim ───────
// Modeled on reader/progress.js: members are the durable record (server rows);
// guests mirror best-score into localStorage and claim their rows on sign-in.

const ANON_TOKEN_KEY = 'ab_anon_token' // shared with reader/practice.js
const BEST_PREFIX = 'ab_live_best_'
const ROUNDS_PREFIX = 'ab_live_rounds_'

// Read-only: the stored token or null, never minting one. claimRounds() must
// use this — a signed-in member with no guest history would otherwise mint a
// brand-new token on every page load just to claim zero rows with it.
function readAnonToken () {
  try {
    const token = localStorage.getItem(ANON_TOKEN_KEY)
    return (token && /^[A-Za-z0-9_-]{8,64}$/.test(token)) ? token : null
  } catch (e) { return null }
}

function anonToken () {
  try {
    let token = localStorage.getItem(ANON_TOKEN_KEY)
    if (!token || !/^[A-Za-z0-9_-]{8,64}$/.test(token)) {
      token = Array.from(crypto.getRandomValues(new Uint8Array(16)),
        b => 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-'[b & 63]).join('')
      localStorage.setItem(ANON_TOKEN_KEY, token)
    }
    return token
  } catch (e) { return null }
}

function csrfToken () {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : null
}

function isMember () {
  const root = document.querySelector('[data-nb-member]')
  return !!(root && root.dataset.nbMember === '1')
}

function postJSON (url, payload, opts) {
  const token = csrfToken()
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    // keepalive lets a request fired right before the tab closes still land
    // (the round capture races the learner closing the tab after the review).
    keepalive: !!(opts && opts.keepalive),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {})
    },
    body: JSON.stringify(payload)
  })
}

function getJSON (url) {
  return fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } })
    .then((r) => { if (!r.ok) throw new Error('request failed (' + r.status + ')'); return r.json() })
}

function readInt (key) {
  try { return parseInt(localStorage.getItem(key) || '0', 10) || 0 } catch (e) { return 0 }
}

function localBest (blockId) { return readInt(BEST_PREFIX + blockId) }
function localRounds (blockId) { return readInt(ROUNDS_PREFIX + blockId) }

// The latest written review per block. The review text (score breakdown +
// "say it stronger" rewrites) used to vanish the moment the stage closed —
// only the bare score survived, server-side. Keeping the last one local makes
// it reachable again from the card ("Last review" chip -> openLastReviewModal).
const REVIEW_PREFIX = 'ab_live_review_'

function storeReviewLocal (blockId, review) {
  try { localStorage.setItem(REVIEW_PREFIX + blockId, JSON.stringify(review)) } catch (e) { /* storage blocked */ }
}

function readReviewLocal (blockId) {
  try { return JSON.parse(localStorage.getItem(REVIEW_PREFIX + blockId) || 'null') } catch (e) { return null }
}

function paintReviewLink (card) {
  const r = readReviewLocal(card.dataset.blockId)
  if (!r) return
  let link = card.querySelector('.nb-scenario-review-link')
  if (!link) {
    link = el('button', 'nb-scenario-chip nb-scenario-review-link')
    link.type = 'button'
    link.addEventListener('click', () => openLastReviewModal(card))
    const meta = card.querySelector('.nb-scenario-meta')
    if (meta) meta.appendChild(link)
  }
  link.textContent = (typeof r.score === 'number') ? `Last review ${r.score}/5` : 'Last review'
}

function recordLocal (blockId, score) {
  try {
    if (typeof score === 'number') {
      localStorage.setItem(BEST_PREFIX + blockId, String(Math.max(localBest(blockId), score)))
    }
    localStorage.setItem(ROUNDS_PREFIX + blockId, String(localRounds(blockId) + 1))
  } catch (e) { /* storage blocked */ }
}

function paintBadge (card) {
  const blockId = card.dataset.blockId
  // Members also get server-rendered stats on the card (data-server-*), so the
  // badge survives a new device where localStorage is empty; take the max of
  // both mirrors (local can be ahead right after a round, server across devices).
  const serverBest = parseInt(card.dataset.serverBest || '', 10)
  const serverRounds = parseInt(card.dataset.serverRounds || '', 10)
  const rounds = Math.max(localRounds(blockId), isNaN(serverRounds) ? 0 : serverRounds)
  if (!rounds) return
  let badge = card.querySelector('.nb-scenario-best')
  if (!badge) {
    badge = el('span', 'nb-scenario-chip nb-scenario-best')
    const meta = card.querySelector('.nb-scenario-meta')
    if (meta) meta.appendChild(badge)
  }
  const best = Math.max(localBest(blockId), isNaN(serverBest) ? 0 : serverBest)
  badge.textContent = best ? `Best ${best}/5 · ${rounds} round${rounds > 1 ? 's' : ''}` : `${rounds} round${rounds > 1 ? 's' : ''}`
}

// The card's "how a round works" strip renders hidden and is revealed only
// for a visitor with zero rounds on this scenario (local mirror + the
// server-rendered member stats, same two sources as paintBadge). Veterans
// never see it; no-JS pages never flash it.
function paintHowItWorks (card) {
  const how = card.querySelector('.nb-scenario-how')
  if (!how) return
  const serverRounds = parseInt(card.dataset.serverRounds || '', 10)
  const rounds = Math.max(localRounds(card.dataset.blockId), isNaN(serverRounds) ? 0 : serverRounds)
  how.hidden = rounds > 0 // also re-hides it the moment their first round is captured
}

// Fire-and-forget: practice UX never blocks on capture. `score` may be null
// when the review call couldn't run. `meta` is the same object showReview
// received; the checklist counts in it are tool-call ground truth from the
// round (null when the scenario has no such checklist, and the server keeps
// that NULL distinct from 0).
function captureRound (card, score, meta) {
  // Custom interviews (Phase L5a) capture to their own counters endpoint and
  // skip the localStorage best-mirror: the ab_ci_history entry stores the
  // server-reported best/rounds instead.
  if (card.dataset.ciId) {
    gaEvent('ci_round_complete', Object.assign({}, (typeof score === 'number' ? { score } : {})))
    postJSON(`/api/v1/custom_interviews/${card.dataset.ciId}/round`, {
      score: (typeof score === 'number' ? score : null),
      duration_seconds: meta.durationSeconds,
      anon_token: anonToken()
    }, { keepalive: true })
      .then(r => r.json())
      .then((d) => { if (d && d.success) updateCiHistoryStats(card.dataset.ciId, d.best, d.rounds) })
      .catch(() => {})
    return
  }
  const blockId = card.dataset.blockId
  recordLocal(blockId, score)
  paintBadge(card)
  paintHowItWorks(card)
  postJSON('/api/v1/scenario_attempts', {
    scenario_attempt: {
      block_id: blockId,
      score: (typeof score === 'number' ? score : null),
      duration_seconds: meta.durationSeconds,
      transport: meta.transport,
      steps_completed: meta.totalSteps ? meta.stepsCompleted : null,
      steps_total: meta.totalSteps || null,
      criteria_completed: meta.totalCriteria ? meta.criteriaCompleted : null,
      criteria_total: meta.totalCriteria || null,
      anon_token: anonToken()
    }
  }, { keepalive: true }).catch(() => {})
}

// After sign-in, hand our guest rounds to the account (client-driven, like the
// page_completions merge). Runs once per page load if signed in.
function claimRounds () {
  if (!isMember()) return
  const token = readAnonToken() // read-only: don't mint a token just to claim nothing
  if (!token) return
  postJSON('/api/v1/scenario_attempts/claim', { scenario_attempt: { anon_token: token } }).catch(() => {})
}

// ── BYOK key: validation ping + onboarding panel ────────────────────────────

// Cheapest possible round-trip: countTokens on the review model. Returns a
// verdict, not a boolean: a 429 means the key is REAL but currently
// rate-limited — telling that user "check you copied it fully" (the old
// behaviour) sent them re-pasting a perfectly good key. The key travels in the
// x-goog-api-key header, not the query string, so it stays out of proxy logs
// and devtools URL bars (the REST calls below do the same; only the WebSocket
// has no header option).
async function validateKey (key, reviewModel) {
  try {
    const res = await fetch(`${GEMINI_HOST}/v1beta/models/${reviewModel}:countTokens`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-goog-api-key': key },
      body: JSON.stringify({ contents: [{ parts: [{ text: 'ping' }] }] })
    })
    if (res.ok) return 'ok'
    if (res.status === 429) return 'quota'
    return 'bad'
  } catch (e) { return 'network' }
}

// The debounced validate-as-you-type wiring, shared by the onboarding panel
// (ensureKey) and the settings modal (openSettingsModal) — it was duplicated
// verbatim between them. Calls onResult(keyOrNull, inputIsEmpty) whenever the
// usable-key state changes: a usable key ('ok', or 'quota' — valid, just
// rate-limited right now), or null (empty / invalid / still checking).
function wireKeyValidation (input, status, getModel, onResult) {
  let timer = null
  const run = () => {
    const key = input.value.trim()
    onResult(null, !key) // checking (or cleared) — not usable yet
    if (!key) { status.textContent = ''; status.className = 'nb-live-key-status'; return }
    status.textContent = 'Checking key…'
    status.className = 'nb-live-key-status'
    validateKey(key, getModel()).then((verdict) => {
      if (input.value.trim() !== key) return // superseded by newer input
      if (verdict === 'ok') {
        status.textContent = 'Key looks good.'
        status.className = 'nb-live-key-status is-ok'
        onResult(key, false)
      } else if (verdict === 'quota') {
        status.textContent = "Key is valid, but it's hit Google's free-tier rate limit right now. You can save it; practice may fail until the quota resets (midnight Pacific time)."
        status.className = 'nb-live-key-status is-ok'
        onResult(key, false)
      } else if (verdict === 'network') {
        status.textContent = 'Could not reach Google to check the key.'
        status.className = 'nb-live-key-status is-bad'
      } else {
        status.textContent = 'That key was rejected. Check you copied it fully.'
        status.className = 'nb-live-key-status is-bad'
      }
    })
  }
  input.addEventListener('input', () => {
    clearTimeout(timer)
    timer = setTimeout(run, 500)
  })
}

// Resolves with { key, teaser } — a usable key (stored or freshly
// entered+validated), or a minted teaser ephemeral token when the visitor has
// no key and takes the free demo. Rejects if the user backs out.
function ensureKey (stage, brief, gaCtx, allowTeaser) {
  const existing = readKey()
  if (existing) {
    gaEvent('live_key_present', gaCtx)
    return Promise.resolve({ key: existing, teaser: false })
  }

  return new Promise((resolve, reject) => {
    const panel = el('div', 'nb-live-panel')
    panel.innerHTML = `
      <h4>Bring your own free AI key</h4>
      <p>This practice runs on Google's free Gemini tier using <strong>your</strong> key, so it stays free for everyone. It takes about 30 seconds:</p>
      <ol>
        <li>Open <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">Google AI Studio &rarr; Create API key</a> (free, no card needed).</li>
        <li>Copy the key and paste it below.</li>
      </ol>
      <input type="password" class="nb-live-key-input" placeholder="Paste your Gemini API key" autocomplete="off" spellcheck="false">
      <p class="nb-live-key-status" role="status" aria-live="polite"></p>
      <label class="nb-live-remember"><input type="checkbox"> Remember on this device</label>
      <p style="font-size:0.75rem;color:var(--muted);margin:-0.5rem 0 1rem">Your key stays in this browser and is sent only to Google. We never receive it.</p>
      <div class="nb-live-panel-actions">
        <button type="button" class="nb-live-primary" disabled>Start practice</button>
        <button type="button" class="nb-live-secondary">Cancel</button>
      </div>
      ${allowTeaser ? '<p class="nb-live-teaser-wrap" style="margin-top:1rem;border-top:1px solid var(--hair);padding-top:1rem;font-size:0.85rem;color:var(--ink-soft)">No key yet? <button type="button" class="nb-live-secondary nb-live-teaser">Try one free demo round</button> (voice only, limited per day).</p>' : ''}`

    const input = panel.querySelector('.nb-live-key-input')
    const status = panel.querySelector('.nb-live-key-status')
    const remember = panel.querySelector('.nb-live-remember input')
    const startBtn = panel.querySelector('.nb-live-primary')
    const cancelBtn = panel.querySelector('.nb-live-secondary')
    const teaserBtn = panel.querySelector('.nb-live-teaser')

    if (teaserBtn) {
      teaserBtn.addEventListener('click', () => {
        teaserBtn.disabled = true
        status.textContent = 'Starting a free demo round…'
        status.className = 'nb-live-key-status'
        postJSON('/api/v1/live_tokens', { anon_token: anonToken() })
          .then(r => r.json())
          .then((d) => {
            if (d.success && d.token) {
              gaEvent('live_key_present', Object.assign({}, gaCtx, { tier: 'teaser' }))
              resolve({ key: d.token, teaser: true })
            } else if (d.reason === 'cap') {
              status.textContent = d.info || 'Free demo limit reached for today.'
              status.className = 'nb-live-key-status is-bad'
              teaserBtn.disabled = false
            } else if (d.reason === 'disabled') {
              // Kill switch is on but this page was cached with the card still
              // rendered: give the honest message, not a "paste your key" nudge.
              status.textContent = d.info || 'Live practice is temporarily unavailable.'
              status.className = 'nb-live-key-status is-bad'
              const wrap = panel.querySelector('.nb-live-teaser-wrap')
              if (wrap) wrap.remove()
            } else {
              const wrap = panel.querySelector('.nb-live-teaser-wrap')
              if (wrap) wrap.remove()
              status.textContent = 'Free demo is unavailable right now. Paste your own key to practise.'
            }
          })
          .catch(() => {
            status.textContent = 'Could not start the demo. Paste your key instead.'
            teaserBtn.disabled = false
          })
      })
    }

    let validated = null
    wireKeyValidation(input, status, () => brief.validate_model || brief.review_model, (key) => {
      validated = key
      startBtn.disabled = !key
    })
    startBtn.addEventListener('click', () => {
      if (!validated) return
      storeKey(validated, remember.checked)
      gaEvent('live_key_present', gaCtx)
      resolve({ key: validated, teaser: false })
    })
    cancelBtn.addEventListener('click', () => reject(new Error('cancelled')))

    setBody(stage, panel)
    input.focus()
  })
}

// Shared overlay-modal scaffolding: dialog semantics, click-outside close, a
// Tab focus trap, and focus restore on close. These overlays used to be bare
// divs — Tab wandered behind them and screen readers never heard "dialog".
// The close function is stashed on the overlay node so the global Escape
// handler (init) can close through the same path and restore focus too.
function modalOverlay (panel) {
  const overlay = el('div', 'nb-key-modal-overlay')
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  overlay.appendChild(panel)
  document.body.appendChild(overlay)
  const opener = document.activeElement
  const close = () => {
    overlay.remove()
    if (opener && typeof opener.focus === 'function') { try { opener.focus() } catch (e) { /* opener gone */ } }
  }
  overlay._close = close
  // Move focus into the dialog (callers may re-focus a specific field after).
  const auto = panel.querySelector('a[href], button:not([disabled]), input:not([disabled]), select')
  if (auto) auto.focus()
  // Route through overlay._close (not the captured close) so a caller that
  // wraps _close — firstRunSetup resolving its promise on dismissal — sees
  // outside-clicks too, not just the global Escape handler.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay._close() })
  overlay.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return
    const focusables = panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])')
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() } else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  })
  // The returned close delegates too, so all three dismissal paths (caller,
  // outside-click, Escape) run the same, possibly-wrapped, _close.
  return { overlay, close: () => overlay._close() }
}

// Practice settings dialog (the gear icon, reachable both on the pre-round
// card and from inside the immersive stage's topbar): learner name + practice
// language (spec §Personalization), plus the same "change/add AI key" flow
// that used to be its own modal. Name/language changes apply to the NEXT
// round fetched (start() / ctx._replay() read readPrefs() fresh each time);
// they don't touch a round already in progress. Replacing the key also clears
// the model-skip cooldown (readModelSkip/skipModel) since a new key may have
// access to models the old one was just denied on.
//
// `midRound` (true when opened from the stage topbar, i.e. the stage is
// already open, whether still on the pre-start briefing or already talking)
// shows a stronger, separate callout: this is exactly the case that confused
// a learner who changed the language here and heard no difference, the stage
// was already built from the OLD brief and a settings save doesn't rebuild it.
function openSettingsModal (card, opts) {
  const midRound = !!(opts && opts.midRound)
  const gaCtx = gaContext(card)
  const panel = el('div', 'nb-live-panel nb-key-modal')
  const hadKey = !!readKey()
  const prefs = readPrefs()
  panel.innerHTML = `
    <h4>Practice settings</h4>
    ${midRound ? '<p class="nb-live-notice">This round is already open, so name/language changes here won\'t affect it. End (or close) this round and start a new one to hear the change.</p>' : ''}
    <p>Personalize how the coach talks to you and pick your practice language. Takes effect on your next round.</p>
    <label class="nb-live-field-label">What should the coach call you? <span style="font-weight:400">(optional)</span></label>
    <input type="text" class="nb-live-key-input nb-live-name-input" placeholder="Your name" maxlength="40" autocomplete="off">
    <label class="nb-live-field-label">Practice language</label>
    <select class="nb-live-select nb-live-lang-select"></select>
    <label class="nb-live-field-label">Round length</label>
    <select class="nb-live-select nb-live-duration-select"></select>
    <p style="font-size:0.75rem;color:var(--muted);margin:0.25rem 0 0">On rounds past 15 minutes the coach may not recall the very first minutes word-for-word.</p>
    <hr class="nb-live-settings-divider">
    <label class="nb-live-field-label">${hadKey ? 'Change your AI key' : 'Add your AI key'}</label>
    <p style="margin-top:-0.4rem">Paste a <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener">Google AI Studio</a> key to ${hadKey ? 'replace the one saved in this browser' : 'use for live practice'}. Leave blank to keep your current key.</p>
    <input type="password" class="nb-live-key-input" placeholder="Paste your Gemini API key" autocomplete="off" spellcheck="false">
    <p class="nb-live-key-status" role="status" aria-live="polite"></p>
    <label class="nb-live-remember"><input type="checkbox" checked> Remember on this device</label>
    <div class="nb-live-panel-actions">
      <button type="button" class="nb-live-primary" data-save>Save</button>
      ${hadKey ? '<button type="button" class="nb-live-secondary" data-remove-key>Remove saved key</button>' : ''}
      <button type="button" class="nb-live-secondary" data-cancel-key>Cancel</button>
    </div>
    <p style="font-size:0.75rem;color:var(--muted);margin:0.75rem 0 0">Your key stays in this browser and is sent only to Google. We never receive it.</p>`
  const { close } = modalOverlay(panel)

  const nameInput = panel.querySelector('.nb-live-name-input')
  const langSelect = panel.querySelector('.nb-live-lang-select')
  const durationSelect = panel.querySelector('.nb-live-duration-select')
  nameInput.value = prefs.name || ''

  const input = panel.querySelector('.nb-live-key-input:not(.nb-live-name-input)')
  const status = panel.querySelector('.nb-live-key-status')
  const remember = panel.querySelector('.nb-live-remember input')
  const saveBtn = panel.querySelector('[data-save]')
  const removeBtn = panel.querySelector('[data-remove-key]')
  const cancelBtn = panel.querySelector('[data-cancel-key]')

  cancelBtn.addEventListener('click', close)
  if (removeBtn) {
    removeBtn.addEventListener('click', () => {
      try { localStorage.removeItem(KEY_NAME); sessionStorage.removeItem(KEY_NAME) } catch (e) { /* storage blocked */ }
      try { localStorage.removeItem(MODEL_SKIP_KEY) } catch (e) { /* storage blocked */ }
      close()
    })
  }

  let validatedKey = null
  cardBrief(card).catch(() => null).then((brief) => {
    const langs = (brief && Array.isArray(brief.supported_languages) && brief.supported_languages.length)
      ? brief.supported_languages
      : [{ code: 'en-US', label: 'English' }]
    langSelect.innerHTML = langs.map(l => `<option value="${escHtml(l.code)}">${escHtml(l.label)}</option>`).join('')
    langSelect.value = prefs.language || (brief && brief.language) || 'en-US'
    if (!langSelect.value) langSelect.selectedIndex = 0

    // Round length: server-driven menu (5/10/15/20), same source-of-truth
    // pattern as the language list. Default = the scenario's own length.
    const durations = (brief && Array.isArray(brief.duration_choices) && brief.duration_choices.length)
      ? brief.duration_choices
      : [5, 10, 15, 20]
    durationSelect.innerHTML = durations.map(m => `<option value="${m}">${m} minutes${m === 5 ? ' (default)' : ''}</option>`).join('')
    durationSelect.value = String(prefs.duration || (brief && brief.duration_minutes) || 5)
    if (!durationSelect.value) durationSelect.selectedIndex = 0

    const validateModel = (brief && (brief.validate_model || brief.review_model)) || 'gemini-3.1-flash-lite'
    // Unlike onboarding, an empty input is a valid save here ("keep my key").
    wireKeyValidation(input, status, () => validateModel, (key, empty) => {
      validatedKey = key
      saveBtn.disabled = !key && !empty
    })
  })

  saveBtn.addEventListener('click', () => {
    storePrefs({ name: nameInput.value.trim().slice(0, 40), language: langSelect.value, duration: parseInt(durationSelect.value, 10) || 0 })
    if (validatedKey) {
      try { localStorage.removeItem(MODEL_SKIP_KEY) } catch (e) { /* storage blocked */ }
      storeKey(validatedKey, remember.checked)
      gaEvent('live_key_present', gaCtx)
    }
    gaEvent('live_settings_saved', gaCtx)
    close()
  })

  nameInput.focus()
}

// Learner-visible scene briefing ("Scene notes" button, reachable both on the
// pre-round card and from inside the immersive stage's topbar): the
// situation, who the learner is playing, who they're talking to, and their
// goal. A learner can get caught off guard when the persona references a
// backstory detail baked into the scenario's setting/goal text (e.g. asking
// "how did the presentation go?" when the authored setting already says it
// was chaotic) — this lets them look the premise back up mid-round instead
// of guessing blind. Read-only: no save button, just a close. Deliberately
// excludes complications and success_criteria (see LiveScenarioBrief#learner_brief).
function openBriefModal (brief) {
  const b = (brief && brief.learner_brief) || {}
  const panel = el('div', 'nb-live-panel nb-key-modal')
  const rows = [
    ['The situation', b.setting],
    ['Who you are', b.learner_role],
    ["Who you're talking to", b.persona],
    ['Your goal', b.goal]
  ].filter(([, value]) => value)
  panel.innerHTML = `
    <h4>Scene notes</h4>
    ${rows.length
      ? rows.map(([label, value]) => `<p class="nb-live-field-label">${escHtml(label)}</p><p class="nb-live-brief-value">${escHtml(value)}</p>`).join('')
      : '<p>No extra notes for this scene, just jump in.</p>'}
    <div class="nb-live-panel-actions">
      <button type="button" class="nb-live-secondary" data-close>Close</button>
    </div>`
  const { close } = modalOverlay(panel)
  panel.querySelector('[data-close]').addEventListener('click', close)
}

// One-time welcome step, shown on the visitor's very first start click
// (before the brief is even fetched, so the language they pick here shapes
// their first round). Two jobs: set expectations (a first-timer has no idea a
// round means scene -> talk -> scored review, or that goals tick off live),
// and surface the name/language settings at the exact moment they matter
// instead of leaving them buried behind the gear icon. Resolves true to
// continue into the round, false if the visitor backs out (Escape /
// outside-click); the seen-flag is only set on Continue, since backing out
// also abandons the round itself and the next deliberate start should get
// the welcome again.
function firstRunSetup (card, gaCtx) {
  return new Promise((resolve) => {
    const prefs = readPrefs()
    const panel = el('div', 'nb-live-panel nb-key-modal')
    panel.innerHTML = `
      <h4>Your first live practice round</h4>
      <p>You're about to roleplay a real conversation with an AI coach. Every round has the same shape:</p>
      <ol class="nb-live-onboard-steps">
        <li><strong>Read your scene.</strong> Who you are, who you're talking to, and the goals to complete. Reopen it any time with the &#128221; Scene notes button.</li>
        <li><strong>Talk it out.</strong> Speak (or type) naturally; your goals tick off live at the top of the screen as you complete them.</li>
        <li><strong>Get your review.</strong> A score out of 5, what landed, and stronger ways to say your weakest lines.</li>
      </ol>
      <label class="nb-live-field-label">What should the coach call you? <span style="font-weight:400">(optional)</span></label>
      <input type="text" class="nb-live-key-input nb-live-name-input" placeholder="Your name" maxlength="40" autocomplete="off">
      <label class="nb-live-field-label">Practice language</label>
      <select class="nb-live-select nb-live-lang-select"></select>
      <div class="nb-live-panel-actions">
        <button type="button" class="nb-live-primary" data-continue>Continue</button>
      </div>
      <p style="font-size:0.75rem;color:var(--muted);margin:0.75rem 0 0">Change these any time via the &#9881; Settings button, on the card or during a round.</p>`

    const { overlay, close } = modalOverlay(panel)
    let settled = false
    const base = overlay._close
    overlay._close = () => { base(); if (!settled) { settled = true; resolve(false) } }

    const nameInput = panel.querySelector('.nb-live-name-input')
    const langSelect = panel.querySelector('.nb-live-lang-select')
    nameInput.value = prefs.name || ''
    // Same language-list source as the settings modal; a failed fetch still
    // leaves a working English-only select rather than blocking the welcome.
    cardBrief(card).catch(() => null).then((brief) => {
      const langs = (brief && Array.isArray(brief.supported_languages) && brief.supported_languages.length)
        ? brief.supported_languages
        : [{ code: 'en-US', label: 'English' }]
      langSelect.innerHTML = langs.map(l => `<option value="${escHtml(l.code)}">${escHtml(l.label)}</option>`).join('')
      langSelect.value = prefs.language || (brief && brief.language) || 'en-US'
      if (!langSelect.value) langSelect.selectedIndex = 0
    })

    panel.querySelector('[data-continue]').addEventListener('click', () => {
      settled = true
      storePrefs({ name: nameInput.value.trim().slice(0, 40), language: langSelect.value })
      markFlag(SETUP_KEY)
      gaEvent('live_onboard_done', gaCtx)
      base()
      resolve(true)
    })
    nameInput.focus()
  })
}

// Pre-round briefing: shown as the stage's first screen (spec change
// 2026-07-09, user request) so a learner never gets dropped straight into a
// live conversation with no idea what's going on, the same premise + task
// list that openBriefModal/openCriteriaModal otherwise require a click to
// find are now the very first thing on screen, with a single "Start
// practising" button gating the mic/key/connection work that follows. Shown
// again on every round (including "practise again" replays) since the goal
// escalates each time.
function renderBriefing (stage, brief, onStart) {
  const lb = brief.learner_brief || {}
  const steps = Array.isArray(brief.steps) ? brief.steps.filter(Boolean) : []
  const patterns = Array.isArray(brief.target_patterns) ? brief.target_patterns.filter(Boolean) : []
  const rows = [
    ['The situation', lb.setting],
    ['Who you are', lb.learner_role],
    ["Who you're talking to", lb.persona],
    ['Your goal', lb.goal]
  ].filter(([, value]) => value)

  const panel = el('div', 'nb-live-panel')
  panel.innerHTML = `
    <h4>Before you start</h4>
    ${rows.length
      ? rows.map(([label, value]) => `<p class="nb-live-field-label">${escHtml(label)}</p><p class="nb-live-brief-value">${escHtml(value)}</p>`).join('')
      : '<p>No extra notes for this scene, just jump in.</p>'}
    ${steps.length ? `<p class="nb-live-field-label">&#9989; Your goals</p><ol class="nb-live-brief-list">${steps.map(s => `<li>${escHtml(s)}</li>`).join('')}</ol><p class="nb-live-brief-hint">These tick off at the top of your screen as you complete each one.</p>` : ''}
    ${patterns.length ? `<p class="nb-live-field-label">&#127919; Grammar patterns to demonstrate</p><ol class="nb-live-brief-list">${patterns.map(p => `<li>${escHtml(p)}</li>`).join('')}</ol><p class="nb-live-brief-hint">Tracked live; tap the &#127919; counter at the top any time to see which ones you've hit.</p>` : ''}
    <div class="nb-live-panel-actions">
      <button type="button" class="nb-live-primary" data-start>Start practising</button>
    </div>
    <p class="nb-live-brief-hint" style="margin:0.9rem 0 0">During the round: &#128221; reopens these notes, &#9881; changes your name, language, or key.</p>`
  setBody(stage, panel)
  panel.querySelector('[data-start]').addEventListener('click', onStart)
}

// ── audio: capture (worklet) + playback (scheduled buffer sources) ──────────

class AudioIO {
  constructor (audioContext) {
    this.ctx = audioContext
    this.nextStart = 0
    this.sources = []
    this.onChunk = null
    this.muted = false
    this.playing = false        // model audio is actually coming out of the speaker
  }

  _setPlaying (v) { this.playing = v }

  async startCapture (stream) {
    await this.ctx.audioWorklet.addModule('/live/pcm-capture-processor.js')
    this.micSource = this.ctx.createMediaStreamSource(stream)
    this.worklet = new AudioWorkletNode(this.ctx, 'pcm-capture-processor')
    this.worklet.port.onmessage = (e) => {
      if (this.muted || !this.onChunk) return
      this.onChunk(e.data) // ArrayBuffer of Int16 PCM @ 16 kHz
    }
    this.micSource.connect(this.worklet)
    // Do NOT connect the worklet to the destination — capture only.
  }

  // Queue a 24 kHz PCM16 chunk for gapless playback.
  playChunk (int16) {
    const buf = this.ctx.createBuffer(1, int16.length, OUTPUT_RATE)
    const data = buf.getChannelData(0)
    for (let i = 0; i < int16.length; i++) data[i] = int16[i] / 0x8000
    const src = this.ctx.createBufferSource()
    src.buffer = buf
    src.connect(this.ctx.destination)
    const now = this.ctx.currentTime
    const start = Math.max(now, this.nextStart)
    src.start(start)
    this.nextStart = start + buf.duration
    this.sources.push(src)
    src.onended = () => {
      this.sources = this.sources.filter(s => s !== src)
      if (!this.speaking) this._setPlaying(false)
    }
    this._setPlaying(true)
  }

  // Barge-in: flush everything currently scheduled to play.
  flushPlayback () {
    this.sources.forEach(s => { try { s.stop() } catch (e) { /* already stopped */ } })
    this.sources = []
    this.nextStart = 0
    this._setPlaying(false)
  }

  get speaking () { return this.sources.length > 0 || this.nextStart > this.ctx.currentTime }

  teardown () {
    this.flushPlayback()
    try { if (this.worklet) this.worklet.disconnect() } catch (e) {}
    try { if (this.micSource) this.micSource.disconnect() } catch (e) {}
  }
}

// ── Gemini Live (voice) session over the bidi WebSocket ─────────────────────

const MAX_RECONNECTS = 3

// Registered only when the brief carries a `steps` checklist (spec
// §Progression: a visible, gamified goal list). The model calls this the
// instant it judges a step genuinely done; see _onMessage's `toolCall`
// handling below.
const STEP_TOOL_DECLARATION = {
  name: 'mark_step_complete',
  description: 'Call this the instant the learner has ACTUALLY completed one of the scene goals listed in the system prompt (not merely discussed it). Never call it twice for the same goal.',
  parameters: {
    type: 'OBJECT',
    properties: {
      step_index: { type: 'INTEGER', description: 'Zero-based index of the completed goal in the SCENE GOALS list.' }
    },
    required: ['step_index']
  }
}

// Registered only when the brief carries `target_patterns` (success_criteria
// exposed as a live, learner-visible checklist, same shape as steps but a
// separate tool so scene-task tracking and grammar/language-pattern tracking
// stay independent). The model calls this the instant it judges the learner
// has correctly demonstrated one of the target patterns.
const CRITERION_TOOL_DECLARATION = {
  name: 'mark_criterion_met',
  description: 'Call this the instant the learner has ACTUALLY correctly demonstrated one of the target grammar/language patterns listed in the system prompt (not merely attempted it). Never call it twice for the same pattern.',
  parameters: {
    type: 'OBJECT',
    properties: {
      criterion_index: { type: 'INTEGER', description: 'Zero-based index of the demonstrated pattern in the TARGET GRAMMAR / LANGUAGE PATTERNS list.' }
    },
    required: ['criterion_index']
  }
}

class VoiceSession {
  constructor (key, brief, audioIO, handlers, models, opts) {
    this.key = key
    this.brief = brief
    this.io = audioIO
    // handlers: { onOpen, onError, onClose, onFail, onStatus, onCaption,
    // onState, onReconnecting, onLevel, onStep, onCriterion }
    // onState(name) drives the orb: 'listening' | 'hearing' | 'thinking' | 'speaking'.
    this.handlers = handlers
    // Teaser rounds run on an ephemeral token constraint-locked to ONE model,
    // so every other rung of the ladder is rejected at setup by construction —
    // those failures must never write model-skip memos (they'd hide perfectly
    // good models from the next BYOK round for 12h).
    this.teaser = !!(opts && opts.teaser)
    // Ordered live-model ladder; on a setup-time close we advance to the next.
    this.models = (models && models.length) ? models.slice() : [brief.model]
    this._modelIdx = 0
    this._thinkTimer = null     // set while awaiting the model's reply after the learner speaks
    this._loudStreak = 0        // consecutive loud mic chunks while the model speaks (barge-in debounce)
    this.ws = null
    this.userEnded = false      // the learner (or timer) ended the round
    this.opened = false         // setupComplete seen at least once (real connection)
    this.resumeHandle = null    // session-resumption handle (valid ~2h)
    this._reconnects = 0
    this._resuming = false
    this._closeReason = null    // server close reason, surfaced on a failed connect
    this.transcript = [] // { role: 'ai'|'user', text }
    this._aiBuf = ''
    this._userBuf = ''
    this.stepsDone = new Set() // indices the model has reported via mark_step_complete
    this.criteriaDone = new Set() // indices the model has reported via mark_criterion_met
  }

  connect () {
    this.ws = new WebSocket(`${GEMINI_WS}?key=${encodeURIComponent(this.key)}`)
    this.ws.binaryType = 'blob'
    this.ws.onopen = () => this._sendSetup()
    this.ws.onmessage = (e) => this._onMessage(e)
    this.ws.onerror = () => { if (!this.userEnded && !this.resumeHandle) this.handlers.onError(new Error('Live connection error')) }
    this.ws.onclose = (e) => this._handleClose(e)
  }

  // A GoAway (or dropped connection) is survivable: reconnect into the same
  // conversation with the resumption handle rather than dying mid-sentence
  // (spec §Session management). Falls through to onClose once handles run out.
  //
  // But a socket that closes WITHOUT ever completing setup is a real failure
  // (bad model id, rejected config, auth) — surface the server's close reason
  // via onFail instead of pretending the round just had no speech.
  _handleClose (e) {
    if (e && e.reason) this._closeReason = e.reason
    else if (e && e.code && e.code !== 1000 && e.code !== 1005) this._closeReason = `connection closed (code ${e.code})`
    console.debug('[live-practice] ws closed', {
      code: e && e.code,
      reason: this._closeReason,
      userEnded: this.userEnded,
      opened: this.opened,
      hasResumeHandle: !!this.resumeHandle,
      reconnects: this._reconnects,
      turnsSoFar: this.transcript.length
    })
    if (this.userEnded) return
    if (this.resumeHandle && this._reconnects < MAX_RECONNECTS) {
      this._reconnects += 1
      this._resuming = true
      // Tell the round so the countdown pauses: an outage shouldn't eat
      // practice time (the clock resumes on the next setupComplete/onOpen).
      if (this.handlers.onReconnecting) this.handlers.onReconnecting()
      if (this.handlers.onStatus) this.handlers.onStatus('Reconnecting…')
      setTimeout(() => this.connect(), 400)
      return
    }
    // Never connected on this model. Distinguish a real rejection (no access,
    // quota spent, bad config — the server says so via a policy close code or
    // a telling reason string) from a plain network blip: only the rejection
    // is memoized for ~12h. A blip still advances the ladder THIS round, but
    // must not hide the best model for half a day. Teaser rounds never
    // memoize (see constructor).
    if (!this.opened) {
      const code = e && e.code
      const rejected = code === 1002 || code === 1003 || code === 1007 || code === 1008 ||
        /quota|exhaust|rate.?limit|permission|access|api key|unauthor|forbidden|not (found|available|supported)/i.test(this._closeReason || '')
      if (rejected && !this.teaser) skipModel(this._currentModel())
      if (this._modelIdx < this.models.length - 1) {
        this._modelIdx += 1
        if (this.handlers.onStatus) this.handlers.onStatus('Switching voice model…')
        setTimeout(() => this.connect(), 200)
        return
      }
      if (this.handlers.onFail) {
        this.handlers.onFail(this._closeReason || 'The live service closed the connection before the session started.')
        return
      }
    }
    // Connected fine, but the socket then dropped without us ending it (no
    // resumption handle to reconnect with, or reconnects exhausted) — this is
    // a dropped connection, not a quiet room. Tell endRound() so the review
    // doesn't misreport it as "no speech captured". A close that reads as a
    // spent quota gets its own flag: "network hiccup... try again" on a quota
    // close just invites a doomed retry loop.
    this.handlers.onClose(true, /quota|exhaust|rate.?limit/i.test(this._closeReason || ''))
  }

  _currentModel () { return this.models[this._modelIdx] }

  _toolDeclarations () {
    const b = this.brief
    const decls = []
    if (Array.isArray(b.steps) && b.steps.length) decls.push(STEP_TOOL_DECLARATION)
    if (Array.isArray(b.target_patterns) && b.target_patterns.length) decls.push(CRITERION_TOOL_DECLARATION)
    return decls
  }

  _sendSetup () {
    const b = this.brief
    this._send({
      setup: {
        model: `models/${this._currentModel()}`,
        generationConfig: {
          responseModalities: ['AUDIO'],
          // Native-audio models auto-detect language and reject an explicit
          // languageCode (they close the socket at setup). Language is enforced
          // in the system prompt instead; only the voice is set here.
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: b.voice } }
          }
        },
        systemInstruction: { parts: [{ text: b.system_prompt }] },
        inputAudioTranscription: {},
        outputAudioTranscription: {},
        // Turn-taking. Without this we get the server default endpointing, which
        // chops a learner's mid-sentence pauses into several turns and queues a
        // reply for each, so answers arrive a turn or two late and "stack up".
        // endOfSpeech LOW + ~0.8s silence = wait for the learner to actually
        // finish; prefixPadding keeps the first word from being clipped.
        realtimeInputConfig: {
          automaticActivityDetection: {
            startOfSpeechSensitivity: 'START_SENSITIVITY_HIGH',
            endOfSpeechSensitivity: 'END_SENSITIVITY_LOW',
            prefixPaddingMs: 300,
            silenceDurationMs: 800
          }
        },
        // Ask the server for resumption handles, and hand ours back on a
        // reconnect so the conversation continues where it left off.
        sessionResumption: this.resumeHandle ? { handle: this.resumeHandle } : {},
        // Sliding-window compression removes the 15-min audio-context ceiling
        // (spec §Session management).
        contextWindowCompression: { slidingWindow: {} },
        // Only registered when the scenario authored a visible steps checklist
        // and/or target grammar patterns; absent entirely otherwise so
        // existing scenarios are unaffected.
        ...(this._toolDeclarations().length ? { tools: [{ functionDeclarations: this._toolDeclarations() }] } : {})
      }
    })
  }

  _kickoff () {
    // Nudge the persona to open, so the learner isn't met with silence. When
    // the scenario authored a first_line, feed it in — it was built server-side
    // and shipped in the brief but previously never used.
    const opener = this.brief.first_line
      ? ` A good opening line for you: "${this.brief.first_line}"`
      : ''
    this._send({
      clientContent: {
        turns: [{ role: 'user', parts: [{ text: `(The learner has just joined and can hear you. Greet them in character and begin the scene now.${opener})` }] }],
        turnComplete: true
      }
    })
  }

  // Time-keeping nudge. The model has no clock, so the guardrail "end the
  // round gracefully when time is nearly up" was unactionable and the
  // countdown expiring hard-cut it mid-sentence. Sent once (invisible client
  // text, same channel as _kickoff) when ~30s remain, so the persona delivers
  // its in-character sign-off before endRound() fires.
  wrapUp () {
    this._send({
      clientContent: {
        turns: [{ role: 'user', parts: [{ text: '(About 30 seconds of practice time remain. Bring the scene to a natural close now and give your warm one-line sign-off in character.)' }] }],
        turnComplete: true
      }
    })
  }

  sendAudio (arrayBuffer) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return
    const rms = rmsFromBuffer(arrayBuffer)
    // Every chunk's loudness feeds the orb's mic meter (see runVoiceRound's
    // onLevel): the learner can SEE the mic hearing them, the top anxiety in
    // any voice UI.
    if (this.handlers.onLevel) this.handlers.onLevel(rms)
    // Full-duplex, echo-safe barge-in: while the model is speaking, forward the
    // mic ONLY when the learner is clearly talking over it (energy above the
    // residual-echo floor). That drops the model's own playback echoed back
    // (which otherwise gets mis-heard as new turns and stacks up), while still
    // letting a genuine interruption through — and that interruption cuts the
    // model's audio immediately. When the model is silent, forward everything.
    if (this.io.playing) {
      if (rms < BARGE_IN_RMS) { this._loudStreak = 0; return }
      // Debounce: chunks are ~20 ms, so one loud chunk is a cough or a door
      // slam, not an interruption. Require a short run of loud chunks before
      // cutting the model off (the ~40 ms of dropped lead-in is inaudible;
      // the server's prefixPadding re-catches the first word).
      this._loudStreak += 1
      if (this._loudStreak < 3) return
      this._loudStreak = 0
      this.io.flushPlayback() // learner is interrupting — stop the model now
    }
    // `mediaChunks` (plural, array) is deprecated by the API in favour of a
    // single `audio` blob per message; the server closes the socket (code
    // 1007) on the old field the moment the first chunk arrives.
    this._send({
      realtimeInput: {
        audio: { mimeType: 'audio/pcm;rate=16000', data: base64FromBuffer(arrayBuffer) }
      }
    })
  }

  async _onMessage (event) {
    let text
    if (event.data instanceof Blob) text = await event.data.text()
    else text = event.data
    let msg
    try { msg = JSON.parse(text) } catch (e) { return }

    if (msg.setupComplete) {
      this.opened = true
      // A successful (re)connect earns back the reconnect budget; without this
      // a long round that survives several GoAways eventually runs out of
      // reconnects for no reason.
      this._reconnects = 0
      console.debug('[live-practice] setupComplete', { model: this._currentModel() })
      this.io.onChunk = (buf) => this.sendAudio(buf)
      if (this._resuming) {
        this._resuming = false // reconnected: continue, don't greet again
      } else {
        this._kickoff()
      }
      this.handlers.onOpen()
      return
    }

    if (msg.sessionResumptionUpdate) {
      const u = msg.sessionResumptionUpdate
      if (u.resumable && u.newHandle) this.resumeHandle = u.newHandle
      console.debug('[live-practice] sessionResumptionUpdate', { resumable: u.resumable, hasHandle: !!u.newHandle })
      return
    }

    if (msg.goAway) {
      // Server is about to reset (the ~10-min cap). Let the close handler
      // reconnect us into the same conversation via the resumption handle.
      console.debug('[live-practice] goAway')
      return
    }

    if (msg.toolCall) {
      // Every functionCall MUST get a toolResponse or the model stalls waiting
      // for one; dedupe on our side (stepsDone/criteriaDone) since a network
      // hiccup or the model's own judgement could otherwise fire the same
      // goal/pattern twice.
      const calls = Array.isArray(msg.toolCall.functionCalls) ? msg.toolCall.functionCalls : []
      const responses = calls.map((call) => {
        if (call.name === 'mark_step_complete') {
          const idx = call.args && Number(call.args.step_index)
          if (Number.isInteger(idx) && !this.stepsDone.has(idx)) {
            this.stepsDone.add(idx)
            console.debug('[live-practice] step complete', { idx })
            if (this.handlers.onStep) this.handlers.onStep(idx)
          }
        } else if (call.name === 'mark_criterion_met') {
          const idx = call.args && Number(call.args.criterion_index)
          if (Number.isInteger(idx) && !this.criteriaDone.has(idx)) {
            this.criteriaDone.add(idx)
            console.debug('[live-practice] criterion met', { idx })
            if (this.handlers.onCriterion) this.handlers.onCriterion(idx)
          }
        }
        return { id: call.id, name: call.name, response: { result: 'ok' } }
      })
      if (responses.length) this._send({ toolResponse: { functionResponses: responses } })
      return
    }

    const sc = msg.serverContent
    if (!sc) return

    if (sc.interrupted) {
      this._clearThink()
      this.io.flushPlayback() // user talked over the model
      // The output transcription runs ahead of playback, so _aiBuf holds text
      // the learner never actually heard. Flush it now as an explicitly
      // interrupted turn instead of letting turnComplete later record it as
      // fully spoken (the review would otherwise coach against ghost lines).
      if (this._aiBuf.trim()) {
        this.transcript.push({ role: 'ai', text: this._aiBuf.trim(), interrupted: true })
        this._aiBuf = ''
      }
      this._state('hearing')  // the learner has the floor again
    }

    const parts = sc.modelTurn && sc.modelTurn.parts
    if (parts) {
      parts.forEach((p) => {
        const inline = p.inlineData
        if (inline && inline.data) {
          this._clearThink()   // reply arrived — stop showing "thinking"
          this.io.playChunk(int16FromBase64(inline.data))
          this._state('speaking')
        }
      })
    }

    if (sc.outputTranscription && sc.outputTranscription.text) {
      this._aiBuf += sc.outputTranscription.text
      this.handlers.onCaption(this._aiBuf)
    }
    if (sc.inputTranscription && sc.inputTranscription.text) {
      // The learner is talking (the server heard them). Show "hearing", and arm
      // a short timer: once they pause and we're waiting on the model, flip to
      // "thinking" so the round never looks frozen (the top testing complaint).
      this._userBuf += sc.inputTranscription.text
      this._state('hearing')
      this._clearThink()
      this._thinkTimer = setTimeout(() => { this._thinkTimer = null; this._state('thinking') }, 900)
    }

    if (sc.turnComplete || sc.generationComplete) {
      // User speaks before the model replies within a turn, so flush in that
      // chronological order (not ai-then-user) or the transcript reads backwards.
      if (this._userBuf.trim()) { this.transcript.push({ role: 'user', text: this._userBuf.trim() }); this._userBuf = '' }
      if (this._aiBuf.trim()) { this.transcript.push({ role: 'ai', text: this._aiBuf.trim() }); this._aiBuf = '' }
      console.debug('[live-practice] turn flush', { turns: this.transcript.length })
      if (sc.turnComplete) { this._clearThink(); this._state('listening') }
    }
  }

  _state (name) { if (this.handlers.onState) this.handlers.onState(name) }
  _clearThink () { if (this._thinkTimer) { clearTimeout(this._thinkTimer); this._thinkTimer = null } }

  _send (obj) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj))
  }

  close () {
    this.userEnded = true
    this._clearThink()
    this.io.onChunk = null
    if (this.ws) { try { this.ws.close() } catch (e) {} }
    // Flush any partials into the transcript for the review step, in
    // chronological order (the learner's unfinished turn before the model's).
    if (this._userBuf.trim()) this.transcript.push({ role: 'user', text: this._userBuf.trim() })
    if (this._aiBuf.trim()) this.transcript.push({ role: 'ai', text: this._aiBuf.trim() })
  }
}

// ── Text session (second transport, same brief) ─────────────────────────────

class TextSession {
  constructor (key, brief, models, hooks) {
    this.key = key
    this.brief = brief
    this.models = (models && models.length) ? models.slice() : [brief.review_model]
    // hooks: { onStep, onCriterion } — the same live checklist reporting the
    // voice transport gets; generateContent supports the same function
    // declarations, so text rounds no longer render counters that can't move.
    this.hooks = hooks || {}
    this.history = [] // Gemini `contents`
    this.transcript = []
    this.stepsDone = new Set()
    this.criteriaDone = new Set()
  }

  _toolDeclarations () {
    const b = this.brief
    const decls = []
    if (Array.isArray(b.steps) && b.steps.length) decls.push(STEP_TOOL_DECLARATION)
    if (Array.isArray(b.target_patterns) && b.target_patterns.length) decls.push(CRITERION_TOOL_DECLARATION)
    return decls
  }

  // Same dedupe contract as VoiceSession's toolCall handling.
  _handleCall (call) {
    if (call.name === 'mark_step_complete') {
      const idx = call.args && Number(call.args.step_index)
      if (Number.isInteger(idx) && !this.stepsDone.has(idx)) {
        this.stepsDone.add(idx)
        if (this.hooks.onStep) this.hooks.onStep(idx)
      }
    } else if (call.name === 'mark_criterion_met') {
      const idx = call.args && Number(call.args.criterion_index)
      if (Number.isInteger(idx) && !this.criteriaDone.has(idx)) {
        this.criteriaDone.add(idx)
        if (this.hooks.onCriterion) this.hooks.onCriterion(idx)
      }
    }
  }

  // Walk the review-model ladder: on a 429 (daily RPD spent) skip that model for
  // ~12h and drop to the next; the flash-lite floor has ~500/day so it holds up.
  // Within a model, a bounded inner loop resolves function-call turns (checklist
  // progress) before the conversational text comes back. _generate owns ALL
  // history pushes for the model side, so tool turns and the final text land in
  // `contents` exactly once.
  async _generate () {
    let lastErr
    const decls = this._toolDeclarations()
    outer: for (const model of usableModels(this.models)) {
      for (let hop = 0; hop < 4; hop++) {
        let res
        try {
          res = await fetch(`${GEMINI_HOST}/v1beta/models/${model}:generateContent`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-goog-api-key': this.key },
            body: JSON.stringify({
              systemInstruction: { parts: [{ text: this.brief.system_prompt }] },
              contents: this.history,
              generationConfig: { temperature: 0.9 },
              ...(decls.length ? { tools: [{ functionDeclarations: decls }] } : {})
            })
          })
        } catch (e) { lastErr = e; continue outer }
        if (res.status === 429) { skipModel(model); lastErr = new Error('text turn failed (429)'); continue outer }
        if (!res.ok) { lastErr = new Error('text turn failed (' + res.status + ')'); continue outer }
        let data
        try { data = await res.json() } catch (e) { lastErr = e; continue outer }
        const parts = (data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts) || []
        const calls = parts.filter(p => p.functionCall).map(p => p.functionCall)
        const text = parts.filter(p => p.text).map(p => p.text).join('').trim()
        if (!calls.length) {
          this.history.push({ role: 'model', parts: [{ text: text }] })
          return text
        }
        // Checklist progress reported: answer every call (same must-respond
        // contract as the Live socket), then either return the text that came
        // with it or take another hop for the spoken reply.
        calls.forEach((c) => this._handleCall(c))
        this.history.push({ role: 'model', parts: parts })
        this.history.push({
          role: 'user',
          parts: calls.map(c => ({ functionResponse: { name: c.name, response: { result: 'ok' } } }))
        })
        if (text) return text
      }
      lastErr = new Error('text turn failed (tool loop)')
    }
    throw lastErr || new Error('text turn failed')
  }

  async kickoff () {
    const opener = this.brief.first_line ? ` A good opening line for you: "${this.brief.first_line}"` : ''
    this.history.push({ role: 'user', parts: [{ text: `(The learner has just joined. Greet them in character and begin the scene.${opener})` }] })
    const reply = await this._generate()
    this.transcript.push({ role: 'ai', text: reply })
    return reply
  }

  async say (userText) {
    this.history.push({ role: 'user', parts: [{ text: userText }] })
    this.transcript.push({ role: 'user', text: userText })
    const reply = await this._generate()
    this.transcript.push({ role: 'ai', text: reply })
    return reply
  }
}

// ── post-session review (cheap text model, same key) ────────────────────────

async function reviewRound (key, brief, transcript, models, progress) {
  const convo = transcript.map(t => {
    const who = t.role === 'ai' ? (t.interrupted ? 'Coach (cut off mid-line by the learner)' : 'Coach') : 'Learner'
    return `${who}: ${t.text}`
  }).join('\n')
  const criteria = (brief.success_criteria || []).map(c => `- ${c}`).join('\n') || '- Kept the conversation going naturally'
  // The checklist counts tracked live via tool calls are ground truth the
  // reviewer shouldn't have to re-infer from the transcript.
  const trackedLines = []
  if (progress && progress.totalSteps) trackedLines.push(`- Scene goals completed: ${progress.stepsCompleted} of ${progress.totalSteps}`)
  if (progress && progress.totalCriteria) trackedLines.push(`- Target patterns correctly demonstrated: ${progress.criteriaCompleted} of ${progress.totalCriteria}`)
  const tracked = trackedLines.length
    ? `LIVE PROGRESS (tracked during the round by the roleplay model; treat as primary evidence when scoring):\n${trackedLines.join('\n')}\n\n`
    : ''
  const prompt =
    `You are a supportive language/interview coach. A learner, often speaking a second language, just finished this practice round.\n\n` +
    `SUCCESS CRITERIA:\n${criteria}\n\n${tracked}TRANSCRIPT:\n${convo}\n\n` +
    `Score the learner 0 to 5 against the criteria. Then pick up to 3 lines the learner ACTUALLY said that were short, hesitant, or awkward (e.g. "sure, go ahead") and rewrite each as a more natural, fuller sentence they could use next time; keep the rewrite realistic for spoken conversation, not stiff or formal. If the learner barely spoke, still give 1 or 2 upgrades based on what a confident speaker would have said at that moment.\n\n` +
    `Reply with ONLY compact JSON: ` +
    `{"score": <0-5 integer>, "went_well": "<one sentence>", "to_fix": "<one sentence>", "try_next": "<one concrete thing for next round>", "upgrades": [{"you_said": "<what the learner said>", "better": "<a stronger, natural rewrite>", "why": "<3 to 6 words>"}]}.`

  const list = usableModels((models && models.length) ? models : [brief.review_model])
  let lastErr
  for (const model of list) {
    try {
      const res = await fetch(`${GEMINI_HOST}/v1beta/models/${model}:generateContent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-goog-api-key': key },
        body: JSON.stringify({
          contents: [{ role: 'user', parts: [{ text: prompt }] }],
          generationConfig: { responseMimeType: 'application/json', temperature: 0.4 }
        })
      })
      if (res.status === 429) { skipModel(model); lastErr = new Error('quota'); continue }
      if (!res.ok) { lastErr = new Error('review failed'); continue }
      const data = await res.json()
      const raw = data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0].text
      return JSON.parse(raw)
    } catch (e) { lastErr = e }
  }
  throw lastErr || new Error('review failed')
}

// ── immersive stage ─────────────────────────────────────────────────────────

function setBody (stage, node) {
  const body = stage.querySelector('.nb-live-body')
  body.replaceChildren(node)
}

// A scenario can carry a pool of background images (most interviews share one
// office); pick one at random per round so a repeat scene still feels new. Falls
// back to the single background_image. buildStage runs every round, so the pick
// re-rolls each time even when the brief itself came from cache.
function pickBackground (brief) {
  const pool = Array.isArray(brief.background_images) ? brief.background_images.filter(Boolean) : []
  if (pool.length) return pool[Math.floor(Math.random() * pool.length)]
  return brief.background_image || null
}

// Learner-visible scene-goals checklist (spec §Progression: gamified). Only
// rendered when the brief carries `steps`; the model reports progress itself
// via the mark_step_complete tool (VoiceSession._onMessage), so this is
// display-only, never user-editable.
function updateStepsCounter (stage, done, total) {
  const counter = stage.querySelector('.nb-live-steps-counter')
  if (counter) counter.textContent = `✅ ${done}/${total} goals`
}

function markStepDone (stage, idx) {
  const items = stage.querySelectorAll('.nb-live-step')
  const li = items[idx]
  if (!li || li.classList.contains('is-done')) return
  li.classList.add('is-done')
  updateStepsCounter(stage, stage.querySelectorAll('.nb-live-step.is-done').length, items.length)
}

// Live-tracked target grammar/language patterns (brief.target_patterns, from
// success_criteria). Unlike steps' short task chips, these are full-sentence
// rubric lines, too long to sit inline over the orb, so the checklist itself
// lives off-screen (`.nb-live-criteria`, `hidden`) purely as a state store;
// only a compact "X/Y patterns" counter shows in the topbar, and clicking it
// opens `openCriteriaModal` to see which ones and whether each was met.
function updateCriteriaCounter (stage, done, total) {
  const counter = stage.querySelector('.nb-live-criteria-counter')
  if (counter) counter.textContent = `🎯 ${done}/${total} patterns`
}

function markCriterionDone (stage, idx) {
  const items = stage.querySelectorAll('.nb-live-criterion')
  const li = items[idx]
  if (!li || li.classList.contains('is-done')) return
  li.classList.add('is-done')
  updateCriteriaCounter(stage, stage.querySelectorAll('.nb-live-criterion.is-done').length, items.length)
}

// One-time coach mark, shown the first time a round with a goals/patterns
// checklist actually starts: the briefing SAID the counters exist, this
// points at where they live. Sits in flow right under the topbar (above the
// steps chips), self-dismisses, and never comes back once seen.
function maybeShowStageHint (stage) {
  if (flagSeen(STAGE_HINT_KEY)) return
  const mid = stage.querySelector('.nb-live-topbar-mid')
  if (!mid || !mid.children.length) return // scenario has no live checklist
  markFlag(STAGE_HINT_KEY)
  const hint = el('div', 'nb-live-stage-hint')
  hint.setAttribute('role', 'status')
  const text = stage.querySelector('.nb-live-criteria-counter')
    ? 'Your goals tick off up here as you complete them. Tap the \u{1F3AF} counter to see the pattern list.'
    : 'Your goals tick off up here as you complete them.'
  hint.appendChild(el('span', null, text))
  const ok = el('button', 'nb-live-stage-hint-ok', 'Got it')
  ok.type = 'button'
  ok.addEventListener('click', () => hint.remove())
  hint.appendChild(ok)
  stage.querySelector('.nb-live-topbar').insertAdjacentElement('afterend', hint)
  setTimeout(() => { if (hint.isConnected) hint.remove() }, 12000)
}

// Read-only popover listing each target pattern with a live ✓/○ marker, so a
// learner can check "did I actually use this grammar pattern or not" at any
// point mid-round instead of waiting for the post-round review.
function openCriteriaModal (stage) {
  const items = stage.querySelectorAll('.nb-live-criterion')
  const panel = el('div', 'nb-live-panel nb-key-modal')
  const rows = Array.from(items).map((li) =>
    `<p class="nb-live-criterion-row${li.classList.contains('is-done') ? ' is-done' : ''}">${li.classList.contains('is-done') ? '&#10003;' : '&#9675;'} ${escHtml(li.textContent)}</p>`
  ).join('')
  panel.innerHTML = `
    <h4>Target grammar patterns</h4>
    <p>Checked off live as you demonstrate each one correctly.</p>
    ${rows}
    <div class="nb-live-panel-actions">
      <button type="button" class="nb-live-secondary" data-close>Close</button>
    </div>`
  const { close } = modalOverlay(panel)
  panel.querySelector('[data-close]').addEventListener('click', close)
}

function buildStage (brief, personaLabel) {
  const stage = el('div', 'nb-live-stage')
  const bg = pickBackground(brief)
  if (bg) stage.style.backgroundImage = `url("${bg}")`
  const steps = Array.isArray(brief.steps) ? brief.steps.filter(Boolean) : []
  const patterns = Array.isArray(brief.target_patterns) ? brief.target_patterns.filter(Boolean) : []
  stage.innerHTML = `
    <div class="nb-live-topbar">
      <span class="nb-live-persona"></span>
      <div class="nb-live-topbar-mid">
        ${steps.length ? '<span class="nb-live-steps-counter"></span>' : ''}
        ${patterns.length ? '<button type="button" class="nb-live-criteria-counter"></button>' : ''}
      </div>
      <div class="nb-live-topbar-actions">
        <span class="nb-live-timer"></span>
        <button type="button" class="nb-live-mute" hidden title="Mute mic" aria-label="Mute mic">&#127908;</button>
        <button type="button" class="nb-live-brief-btn" title="Scene notes" aria-label="Scene notes">&#128221;</button>
        <button type="button" class="nb-live-settings-btn" title="Practice settings" aria-label="Practice settings">&#9881;</button>
        <button type="button" class="nb-live-end">End round</button>
      </div>
    </div>
    ${steps.length ? '<ol class="nb-live-steps"></ol>' : ''}
    ${patterns.length ? '<ol class="nb-live-criteria" hidden></ol>' : ''}
    <div class="nb-live-body"></div>`
  stage.querySelector('.nb-live-persona').textContent = personaLabel
  if (steps.length) {
    const list = stage.querySelector('.nb-live-steps')
    steps.forEach((s) => list.appendChild(el('li', 'nb-live-step', s)))
    updateStepsCounter(stage, 0, steps.length)
  }
  if (patterns.length) {
    const list = stage.querySelector('.nb-live-criteria')
    patterns.forEach((p) => list.appendChild(el('li', 'nb-live-criterion', p)))
    updateCriteriaCounter(stage, 0, patterns.length)
    stage.querySelector('.nb-live-criteria-counter').addEventListener('click', () => openCriteriaModal(stage))
  }
  document.body.appendChild(stage)
  document.body.classList.add('nb-live-locked')
  return stage
}

function closeStage (stage) {
  stage.remove()
  document.body.classList.remove('nb-live-locked')
}

function highlightPhrases (text, phrases) {
  let out = text.replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]))
  ;(phrases || []).forEach((p) => {
    const words = p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').split(/\s+/).filter(Boolean).slice(0, 2).join('\\s+')
    if (words) out = out.replace(new RegExp(words, 'gi'), m => `<mark>${m}</mark>`)
  })
  return out
}

// The four-line score summary, shared by the live review panel and the
// card's "Last review" modal.
function reviewSummaryHtml (r) {
  return `<span class="nb-live-review-score">${escHtml(r.score)}/5</span><br>` +
    `<strong>What landed:</strong> ${escHtml(r.went_well) || '-'}<br>` +
    `<strong>To fix:</strong> ${escHtml(r.to_fix) || '-'}<br>` +
    `<strong>Next round:</strong> ${escHtml(r.try_next) || '-'}`
}

// "Save to my flashcards": files the round's upgrade lines into a per-user
// deck via the existing decks/flash_cards JSON API (cookie session; the
// button is member-only since guests have no decks). Cross-product loop: the
// review's best output becomes spaced-repetition material.
const UPGRADES_DECK_NAME = 'Live practice notes'

async function saveUpgradesAsFlashcards (upgrades) {
  const decks = await getJSON('/api/v1/decks')
  let deck = (Array.isArray(decks) ? decks : []).find(d => d && d.name === UPGRADES_DECK_NAME)
  if (!deck) {
    const res = await postJSON('/api/v1/decks', {
      deck: { name: UPGRADES_DECK_NAME, description: 'Stronger lines collected from live practice rounds.', status: 'Private' }
    })
    if (!res.ok) throw new Error('deck create failed')
    deck = await res.json()
  }
  let saved = 0
  // Sequential on purpose: FlashCard#sequence is assigned max+1 per deck, so
  // parallel posts race into a sequence-uniqueness failure.
  for (const u of upgrades.slice(0, 3)) {
    if (!u || !u.better) continue
    const res = await postJSON('/api/v1/flash_cards', {
      flash_card: {
        deck_id: deck.id,
        front: u.you_said ? `Say it stronger: "${u.you_said}"` : 'Say it stronger',
        back: u.better + (u.why ? `\n(${u.why})` : ''),
        difficulty: '1'
      }
    })
    if (res.ok) saved += 1
  }
  if (!saved) throw new Error('no cards saved')
  return saved
}

// "Say it stronger next time": the model's natural rewrites of the learner's
// weakest lines. Inserted right under the score so it's the first thing read.
function renderUpgrades (panel, line, upgrades, gaCtx) {
  if (!Array.isArray(upgrades) || !upgrades.length) return
  const wrap = el('div', 'nb-live-upgrades')
  wrap.appendChild(el('p', 'nb-live-upgrades-h', 'Say it stronger next time'))
  let rendered = 0
  upgrades.slice(0, 3).forEach((u) => {
    if (!u || !u.better) return
    const row = el('div', 'nb-live-upgrade')
    row.innerHTML =
      (u.you_said ? `<span class="u-said">${escHtml(u.you_said)}</span> <span class="u-arrow">&rarr;</span> ` : '') +
      `<span class="u-better">${escHtml(u.better)}</span>` +
      (u.why ? ` <span class="u-why">${escHtml(u.why)}</span>` : '')
    wrap.appendChild(row)
    rendered += 1
  })
  if (rendered && isMember()) {
    const save = el('button', 'nb-live-secondary nb-live-save-cards', 'Save to my flashcards')
    save.type = 'button'
    save.addEventListener('click', () => {
      save.disabled = true
      save.textContent = 'Saving…'
      saveUpgradesAsFlashcards(upgrades).then((n) => {
        save.textContent = `Saved ${n} card${n > 1 ? 's' : ''} to "${UPGRADES_DECK_NAME}"`
        gaEvent('live_upgrades_saved', gaCtx)
      }).catch(() => {
        save.disabled = false
        save.textContent = 'Could not save, retry'
      })
    })
    wrap.appendChild(save)
  }
  line.parentNode.insertBefore(wrap, line.nextSibling)
}

// The stored last review, reachable from the card's "Last review" chip any
// time after the stage is gone (paintReviewLink / storeReviewLocal).
function openLastReviewModal (card) {
  const r = readReviewLocal(card.dataset.blockId)
  if (!r) return
  const panel = el('div', 'nb-live-panel nb-key-modal')
  panel.innerHTML = `
    <h4>Last round review</h4>
    <p class="nb-live-review-line"></p>
    <div class="nb-live-panel-actions">
      <button type="button" class="nb-live-secondary" data-close>Close</button>
    </div>`
  const line = panel.querySelector('.nb-live-review-line')
  line.innerHTML = reviewSummaryHtml(r)
  renderUpgrades(panel, line, r.upgrades, gaContext(card))
  const { close } = modalOverlay(panel)
  panel.querySelector('[data-close]').addEventListener('click', close)
}

function showReview (stage, brief, transcript, key, ctx, meta) {
  // Two-column on tablet/desktop (a min-width+min-height media query): the
  // scorecard reads on the left while the full transcript gets its own tall,
  // scrollable column on the right. On phones the grid collapses to the same
  // single stacked column it has always been — the DOM order below is the
  // stacked order, so mobile is unchanged. renderUpgrades still slots the
  // "say it stronger" box in right after .nb-live-review-line (inside the
  // scorecard), and the quota note still inserts just before it.
  const panel = el('div', 'nb-live-panel nb-live-review-panel')
  panel.innerHTML = `
    <div class="nb-live-review-grid">
      <div class="nb-live-review-main">
        <h4>Round review</h4>
        <p class="nb-live-review-line">Scoring your round…</p>
        <p class="nb-live-steps-result"></p>
        <p class="nb-live-criteria-result"></p>
        <div class="nb-live-panel-actions">
          <button type="button" class="nb-live-primary" data-replay>Practise again (harder)</button>
          <button type="button" class="nb-live-secondary" data-done>Done</button>
        </div>
      </div>
      <div class="nb-live-transcript-col">
        <p class="nb-live-transcript-h">Transcript</p>
        <div class="nb-live-transcript"></div>
      </div>
    </div>`

  const tEl = panel.querySelector('.nb-live-transcript')
  // AI turns render plain (escaped); the learner's turns get target phrases
  // highlighted where they used them. Interrupted coach turns are marked: the
  // tail of that text was generated but never actually heard.
  tEl.innerHTML = transcript.map((t) => {
    const cls = t.role === 'ai' ? 'nb-live-t-ai' : 'nb-live-t-user'
    const html = t.role === 'ai' ? highlightPhrases(t.text, []) : highlightPhrases(t.text, ctx.targetPhrases)
    const cut = (t.role === 'ai' && t.interrupted) ? ' <span class="nb-live-t-cut">(you cut in here)</span>' : ''
    return `<p class="${cls}">${html}${cut}</p>`
  }).join('')

  panel.querySelector('[data-done]').addEventListener('click', () => closeStage(stage))
  panel.querySelector('[data-replay]').addEventListener('click', () => { closeStage(stage); ctx._replay() })
  setBody(stage, panel)

  const stepsResult = panel.querySelector('.nb-live-steps-result')
  if (meta.totalSteps) stepsResult.textContent = `Completed ${meta.stepsCompleted} of ${meta.totalSteps} scene goals.`
  else stepsResult.remove()

  const criteriaResult = panel.querySelector('.nb-live-criteria-result')
  if (meta.totalCriteria) criteriaResult.textContent = `Demonstrated ${meta.criteriaCompleted} of ${meta.totalCriteria} target grammar patterns.`
  else criteriaResult.remove()

  const line = panel.querySelector('.nb-live-review-line')
  // A quota close ended the round early; say so wherever it happened, because
  // "network hiccup, try again" on a spent daily quota just loops the failure.
  if (meta.quotaClosed) {
    line.parentNode.insertBefore(
      el('p', 'nb-live-quota-note', "The live voice model's free daily quota ran out, so the session ended early. It resets at midnight Pacific time; you can keep practising as text meanwhile."),
      line
    )
  }
  if (transcript.length === 0) {
    line.textContent = meta.quotaClosed
      ? 'No speech was captured before the quota ran out.'
      : meta.droppedConnection
        ? 'Your voice connection dropped before any speech came through. This is usually a brief network hiccup, not your mic — try again.'
        : 'No speech was captured this round. Try again in a quiet spot.'
    return
  }

  // A teaser round runs on an ephemeral Live token, which can't call the text
  // API the scored review needs. Show the transcript and nudge toward BYOK.
  if (ctx.teaser) {
    line.textContent = 'Demo round complete. Add your own free key to get a scored review and keep practising.'
    captureRound(ctx.card, null, meta)
    return
  }

  reviewRound(key, brief, transcript, usableModels(brief.review_models), meta).then((r) => {
    line.innerHTML = reviewSummaryHtml(r)
    renderUpgrades(panel, line, r.upgrades, ctx.gaCtx)
    const score = (typeof r.score === 'number') ? r.score : parseInt(r.score, 10)
    // Keep the written review reachable after the stage closes ("Last
    // review" chip on the card); only the numeric score goes to the server.
    storeReviewLocal(ctx.card.dataset.blockId, {
      score: isNaN(score) ? null : score,
      went_well: r.went_well,
      to_fix: r.to_fix,
      try_next: r.try_next,
      upgrades: Array.isArray(r.upgrades) ? r.upgrades.slice(0, 3) : []
    })
    paintReviewLink(ctx.card)
    captureRound(ctx.card, isNaN(score) ? null : score, meta)
  }).catch(() => {
    line.textContent = 'Round complete. (Could not fetch a written review this time.)'
    captureRound(ctx.card, null, meta) // the round still counts
  })
}

// ── round orchestration ─────────────────────────────────────────────────────

async function runVoiceRound (card, brief, ctx) {
  const persona = brief.title || 'Live practice'
  const stage = buildStage(brief, persona)
  // ALL round state is declared before any handler that can fire: the topbar
  // End button is live from the first frame, and a click during the briefing
  // or the key panel must see safe values, not a temporal-dead-zone throw
  // (which is exactly what trapped text mode; see runTextRound).
  let stream, audioCtx, io, session, wakeLock, timerId, orbBox, ended = false
  let roundStart = null, pausedMs = 0, pauseStart = null // wall-clock countdown, minus reconnect gaps
  let manualMute = false // the topbar mic-mute toggle (primary users are on phones, often in public)
  // Before the session exists there is nothing to end — the End button just
  // closes the stage (no scored "no speech captured" review, and no teaser
  // mint burned, for a round that never began).
  stage.querySelector('.nb-live-end').addEventListener('click', () => { if (session) endRound(); else closeStage(stage) })
  stage.querySelector('.nb-live-settings-btn').addEventListener('click', () => openSettingsModal(card, { midRound: true }))
  stage.querySelector('.nb-live-brief-btn').addEventListener('click', () => openBriefModal(brief))

  // No mic API at all (in-app webview, or a browser without getUserMedia):
  // voice can never work here, so say so before the briefing/key flow walks
  // the learner toward a mic prompt that cannot appear.
  if (!micSupported()) {
    gaEvent('live_session_error', Object.assign({ transport: 'voice', reason: 'no_getusermedia' }, ctx.gaCtx))
    const panel = el('div', 'nb-live-panel')
    panel.innerHTML = `
      <h4>This browser can't use your mic</h4>
      <p>${inAppBrowser()
        ? 'You\'re inside an app\'s built-in browser, which blocks microphone access. Open this page in Safari or Chrome (look for "Open in browser" in the app\'s share or &#8943; menu) to practise by voice.'
        : 'Voice practice needs microphone access, which this browser doesn\'t support. Try Safari or Chrome, or practise as text below.'}</p>
      <div class="nb-live-panel-actions">
        <button type="button" class="nb-live-primary" data-text>Practise as text</button>
        <button type="button" class="nb-live-secondary" data-close>Close</button>
      </div>`
    panel.querySelector('[data-text]').addEventListener('click', () => { closeStage(stage); runTextRound(card, brief, ctx) })
    panel.querySelector('[data-close]').addEventListener('click', () => closeStage(stage))
    setBody(stage, panel)
    return
  }

  // iOS Safari kills user activation the moment an await crosses a permission
  // prompt, and an AudioContext created outside a gesture starts (and stays)
  // suspended: a completely silent round that looks like a dead mic. So the
  // context is minted HERE, synchronously inside the "Start practising" tap,
  // not after getUserMedia resolves.
  await new Promise((resolve) => renderBriefing(stage, brief, () => {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)()
      audioCtx.resume().catch(() => {}) // must be called in-gesture; fire and forget
    } catch (e) { /* no WebAudio — startCapture's failure panel reports it */ }
    resolve()
  }))

  let key
  try {
    if (ctx.teaser && ctx.teaserKey) {
      // A failed start already minted a teaser token with uses to spare;
      // retry reuses it instead of burning another of the visitor's daily
      // demo mints (endRound clears it once a round actually consumes it).
      key = ctx.teaserKey
    } else {
      const res = await ensureKey(stage, brief, ctx.gaCtx, true)
      key = res.key
      ctx.teaser = res.teaser
      if (res.teaser) {
        ctx.teaserKey = res.key
        ctx.gaCtx = Object.assign({}, ctx.gaCtx, { tier: 'teaser' })
      }
    }
  } catch (e) {
    // Backed out at the key panel: release the briefing-tap context too.
    if (audioCtx) { try { audioCtx.close() } catch (e2) { /* already closed */ } }
    return closeStage(stage)
  }

  // Mic + AudioContext must be created inside the user gesture chain (iOS).
  const status = el('div', 'nb-live-panel')
  status.innerHTML = '<h4>Getting your mic ready…</h4><p>Allow microphone access when your browser asks.</p>'
  setBody(stage, status)
  const cleanup = () => {
    if (ended) return
    ended = true
    clearInterval(timerId)
    if (session) session.close()
    if (io) io.teardown()
    if (stream) stream.getTracks().forEach(t => t.stop())
    if (audioCtx) { try { audioCtx.close() } catch (e) {} }
    if (wakeLock) { try { wakeLock.release() } catch (e) {} }
    document.removeEventListener('visibilitychange', onHide)
  }
  // Barge-in stays on (talking over the model cuts it off) — see
  // VoiceSession.sendAudio. The mic mutes on the manual toggle or a hidden tab.
  const syncMute = () => {
    if (io) io.muted = manualMute || document.hidden
    // A muted mic sends no chunks, so the meter would freeze mid-level.
    if (orbBox && (manualMute || document.hidden)) orbBox.style.setProperty('--mic', '0')
  }
  const onHide = () => {
    syncMute()
    // The OS auto-releases the screen wake lock when the tab hides; take it
    // back when the round is still running and the tab returns, or the second
    // half of a backgrounded mobile round loses its sleep protection.
    if (!document.hidden && !ended && navigator.wakeLock) {
      navigator.wakeLock.request('screen').then((l) => { wakeLock = l }).catch(() => {})
    }
  }

  const totalSteps = Array.isArray(brief.steps) ? brief.steps.filter(Boolean).length : 0
  const totalCriteria = Array.isArray(brief.target_patterns) ? brief.target_patterns.filter(Boolean).length : 0

  function endRound (droppedConnection, quotaClosed) {
    // The "End round" button stays visible during the review panel (only the
    // body swaps), and Escape re-clicks it too, so guard against a repeat
    // call re-running showReview() and firing a second Gemini review request.
    if (ended) return
    // Practice time only: reconnect gaps (pausedMs) don't count.
    const durationSeconds = roundStart ? Math.round((Date.now() - roundStart - pausedMs) / 1000) : 0
    delete ctx.teaserKey // this round consumed the teaser token; a replay mints fresh
    // cleanup() -> session.close() flushes any in-progress (pre-turnComplete)
    // speech into session.transcript, so it must run BEFORE we snapshot the
    // transcript below — otherwise a turn still mid-flight when the round ends
    // (timer expiry, or "End round" clicked right after speaking) is silently
    // dropped and the review wrongly says no speech was captured.
    cleanup()
    const endBtn = stage.querySelector('.nb-live-end')
    if (endBtn) endBtn.style.display = 'none' // review panel has its own Done/replay actions
    const transcript = session ? session.transcript.slice() : []
    const stepsCompleted = totalSteps ? stage.querySelectorAll('.nb-live-step.is-done').length : 0
    const criteriaCompleted = totalCriteria ? stage.querySelectorAll('.nb-live-criterion.is-done').length : 0
    gaEvent('live_session_complete', Object.assign({ transport: 'voice' }, ctx.gaCtx))
    showReview(stage, brief, transcript, key, ctx, { transport: 'voice', durationSeconds, droppedConnection: !!droppedConnection, quotaClosed: !!quotaClosed, stepsCompleted, totalSteps, criteriaCompleted, totalCriteria })
  }

  // The socket closed before it ever connected (bad model id, rejected config,
  // key without Live access). Show the real reason with a retry + a text
  // fallback, instead of the misleading "no speech captured" review that would
  // loop on replay.
  function failRound (reason) {
    cleanup()
    // The truncated server close reason rides along as the canary-in-GA: if
    // Google ever pulls free Live access, this event spikes with a
    // billing/permission-flavoured reason across ALL users. Pair with a GA
    // custom insight alert on live_session_error volume
    // (docs/live-practice-spec.md, "Kill switch & canary").
    gaEvent('live_session_error', Object.assign({ transport: 'voice', reason: String(reason || '').slice(0, 100) }, ctx.gaCtx))
    const panel = el('div', 'nb-live-panel')
    panel.innerHTML = `
      <h4>Couldn't start the live voice session</h4>
      <p class="nb-live-review-line"></p>
      <p style="font-size:0.85rem;color:var(--muted)">This is usually a temporary issue with the live voice service, or your key not having access to the live audio model. You can retry, or practise this scenario as text.</p>
      <div class="nb-live-panel-actions">
        <button type="button" class="nb-live-primary" data-retry>Retry</button>
        <button type="button" class="nb-live-secondary" data-text>Practise as text</button>
        <button type="button" class="nb-live-secondary" data-close>Close</button>
      </div>`
    panel.querySelector('.nb-live-review-line').textContent = safeError(reason)
    panel.querySelector('[data-retry]').addEventListener('click', () => { closeStage(stage); runVoiceRound(card, brief, ctx) })
    panel.querySelector('[data-text]').addEventListener('click', () => { closeStage(stage); runTextRound(card, brief, ctx) })
    panel.querySelector('[data-close]').addEventListener('click', () => closeStage(stage))
    setBody(stage, panel)
  }

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }
    })
  } catch (e) {
    status.innerHTML = `<h4>Microphone blocked</h4><p>${escHtml(micErrorMessage(e))}</p><div class="nb-live-panel-actions"><button type="button" class="nb-live-primary" data-text>Practise as text</button><button type="button" class="nb-live-secondary" data-close>Close</button></div>`
    status.querySelector('[data-close]').addEventListener('click', () => closeStage(stage))
    status.querySelector('[data-text]').addEventListener('click', () => { closeStage(stage); runTextRound(card, brief, ctx) })
    // The context now predates the mic (created in the briefing tap); release
    // it on this dead end, or retries stack contexts toward iOS's small cap.
    return cleanup()
  }
  gaEvent('live_mic_granted', ctx.gaCtx)

  // Normally the context already exists (created in the briefing tap above);
  // this is only the fallback path if that constructor threw.
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)()
  if (audioCtx.state !== 'running') { try { await audioCtx.resume() } catch (e) {} }
  if (audioCtx.state !== 'running') {
    // Still suspended (or iOS "interrupted" after sitting through the key
    // panel): a resume() must come from a direct gesture, so ask for one tap.
    await new Promise((resolve) => {
      const enable = el('div', 'nb-live-panel')
      enable.innerHTML = '<h4>Tap to enable audio</h4><p>Your browser paused audio for this page. One tap turns your mic and speaker on for this round.</p><div class="nb-live-panel-actions"><button type="button" class="nb-live-primary">Enable audio</button></div>'
      enable.querySelector('button').addEventListener('click', () => { audioCtx.resume().then(resolve, resolve) })
      // End round (or Escape, which clicks it) during this wait just removes
      // the stage (no session yet); resolve too, so the guard below can
      // release the already-open mic instead of leaking it.
      stage.querySelector('.nb-live-end').addEventListener('click', resolve, { once: true })
      setBody(stage, enable)
    })
    if (!stage.isConnected) return cleanup()
  }
  io = new AudioIO(audioCtx)
  try { await io.startCapture(stream) } catch (e) {
    status.innerHTML = `<h4>Audio setup failed</h4><p>${safeError(e)}</p>`
    return cleanup()
  }
  try { wakeLock = await navigator.wakeLock.request('screen') } catch (e) { /* not supported — fine */ }
  document.addEventListener('visibilitychange', onHide)

  // Manual mic mute (voice rounds only; the button stays hidden in text mode).
  const muteBtn = stage.querySelector('.nb-live-mute')
  muteBtn.hidden = false
  muteBtn.addEventListener('click', () => {
    manualMute = !manualMute
    syncMute()
    muteBtn.classList.toggle('is-muted', manualMute)
    muteBtn.innerHTML = manualMute ? '&#128263;' : '&#127908;'
    muteBtn.title = manualMute ? 'Unmute mic' : 'Mute mic'
    muteBtn.setAttribute('aria-label', muteBtn.title)
  })

  // Live UI. The orb + its label sit dead-centre (`nb-live-orb-wrap`); the
  // caption is pinned to the bottom by CSS so a long transcript line never
  // shoves the orb off-centre (a testing complaint). The orb has four visual
  // states so the round never just says "Listening" with no other cue.
  const live = el('div', 'nb-live-body-inner')
  orbBox = el('div', 'nb-live-orb-box is-connecting')
  orbBox.append(el('div', 'nb-live-orb-ring'), el('div', 'nb-live-orb'))
  const state = el('div', 'nb-live-state', 'Connecting…')
  const orbWrap = el('div', 'nb-live-orb-wrap')
  orbWrap.append(orbBox, state)
  const caption = el('div', 'nb-live-caption', '')
  live.append(orbWrap, caption)
  setBody(stage, live)

  // idle → learner speaks (hearing) → pause, awaiting reply (thinking) → model
  // replies (speaking) → back to listening. Drives both the orb class + label.
  const STATE_LABELS = { connecting: 'Connecting…', listening: 'Listening', hearing: 'Listening…', thinking: 'Thinking…', speaking: 'Speaking' }
  const setState = (name) => {
    orbBox.className = 'nb-live-orb-box is-' + name
    if (STATE_LABELS[name]) state.textContent = STATE_LABELS[name]
  }

  // Countdown to the round's chosen length (the learner's settings pick or
  // the authored default; rounds longer than the ~10-min Live socket lifetime
  // ride on the resumption reconnect in VoiceSession). Wall-clock based, NOT
  // a decremented counter: background-tab timer throttling made the counter
  // lag real time and its single `=== 30` tick could be skipped entirely.
  // Time inside a "Reconnecting…" gap is excluded (pausedMs), so an outage
  // doesn't eat practice time. At ~30s left the timer turns urgent and the
  // model gets its wrap-up nudge (see VoiceSession.wrapUp), so expiry lands
  // on a sign-off, not a hard cut. The clock only starts at setupComplete
  // (startClock, via onOpen below): connect latency and walking the model
  // ladder shouldn't eat practice time either.
  const durationSecs = (brief.duration_minutes || 5) * 60
  let signoffGrace = 8 // seconds past 0:00 to let a still-playing sign-off finish
  let wrapped = false
  const timerEl = stage.querySelector('.nb-live-timer')
  timerEl.textContent = `${brief.duration_minutes || 5}:00`
  const tick = () => {
    if (roundStart == null) return
    const anchor = pauseStart || Date.now() // frozen while reconnecting
    const elapsed = Math.round((anchor - roundStart - pausedMs) / 1000)
    const remaining = Math.max(durationSecs - elapsed, 0)
    timerEl.textContent = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
    if (!wrapped && remaining <= 30) {
      wrapped = true
      timerEl.classList.add('is-ending')
      if (session && session.opened) session.wrapUp()
    }
    if (remaining <= 0) {
      // If the model's sign-off is still coming out of the speaker, let it
      // finish (bounded) instead of hard-cutting the final words.
      if (io && io.speaking && signoffGrace > 0) { signoffGrace -= 1; return }
      endRound()
    }
  }
  const startClock = () => {
    if (roundStart != null || ended) return
    roundStart = Date.now()
    tick()
    timerId = setInterval(tick, 1000)
  }

  // Teaser (our shared key) can't reach the 3.x live preview; BYOK gets the full
  // ladder. `usableModels` drops any model still cooling from an earlier failure.
  const liveModels = usableModels(ctx.teaser ? brief.live_models_teaser : brief.live_models)
  session = new VoiceSession(key, brief, io, {
    onOpen: () => {
      // Back from a reconnect gap: bank the paused time so the countdown
      // resumes where it froze instead of jumping.
      if (pauseStart != null) { pausedMs += Date.now() - pauseStart; pauseStart = null }
      setState('listening')
      startClock()
      maybeShowStageHint(stage) // no-op after the first time (flag set on show)
    },
    onReconnecting: () => { if (pauseStart == null) pauseStart = Date.now() },
    onError: (err) => { state.textContent = safeError(err) },
    onClose: (dropped, quota) => endRound(dropped, quota),
    onFail: (reason) => failRound(reason),
    onStatus: (t) => { state.textContent = t },
    onCaption: (t) => { caption.textContent = t },
    onState: (name) => setState(name),
    // Mic loudness drives the orb's ring (CSS --mic) so the learner can see
    // the mic hearing them; ~6000 RMS is a comfortably loud speaking level.
    onLevel: (rms) => { orbBox.style.setProperty('--mic', Math.min(1, rms / 6000).toFixed(2)) },
    onStep: (idx) => markStepDone(stage, idx),
    onCriterion: (idx) => markCriterionDone(stage, idx)
  }, liveModels, { teaser: ctx.teaser })
  session.connect()
  gaEvent('live_session_start', Object.assign({ transport: 'voice' }, ctx.gaCtx))
}

async function runTextRound (card, brief, ctx) {
  const stage = buildStage(brief, brief.title || 'Live practice')
  stage.querySelector('.nb-live-timer').textContent = 'Text mode'
  // Round state declared before the End-button handler can fire, same as
  // runVoiceRound. `session` used to be a const created only after the key
  // panel, so clicking End round (or Escape) during the briefing/key panel
  // hit finish() inside the temporal dead zone: an uncaught ReferenceError,
  // and a stage stuck open with no way out short of a reload. Until the
  // session exists, End round just closes the stage.
  let session = null
  let roundStart = null
  let finished = false
  stage.querySelector('.nb-live-end').addEventListener('click', () => { if (session) finish(); else closeStage(stage) })
  stage.querySelector('.nb-live-settings-btn').addEventListener('click', () => openSettingsModal(card, { midRound: true }))
  stage.querySelector('.nb-live-brief-btn').addEventListener('click', () => openBriefModal(brief))

  await new Promise((resolve) => renderBriefing(stage, brief, resolve))

  let key
  try {
    const res = await ensureKey(stage, brief, ctx.gaCtx, false)
    key = res.key
  } catch (e) { return closeStage(stage) }

  const wrap = el('div', 'nb-live-body-inner')
  const thread = el('div', 'nb-live-thread')
  const row = el('div', 'nb-live-input-row')
  const input = el('input', 'nb-live-input')
  input.placeholder = 'Type your reply…'
  const send = el('button', 'nb-live-send', 'Send')
  row.append(input, send)
  wrap.append(thread, row)
  setBody(stage, wrap)

  session = new TextSession(key, brief, usableModels(brief.review_models), {
    onStep: (idx) => markStepDone(stage, idx),
    onCriterion: (idx) => markCriterionDone(stage, idx)
  })
  const totalSteps = Array.isArray(brief.steps) ? brief.steps.filter(Boolean).length : 0
  const totalCriteria = Array.isArray(brief.target_patterns) ? brief.target_patterns.filter(Boolean).length : 0
  roundStart = Date.now()
  gaEvent('live_session_start', Object.assign({ transport: 'text' }, ctx.gaCtx))

  const addMsg = (role, text) => {
    thread.append(el('div', `nb-live-msg nb-live-msg--${role === 'ai' ? 'ai' : 'user'}`, text))
    thread.scrollTop = thread.scrollHeight
  }
  const busy = (b) => { send.disabled = b; input.disabled = b }

  busy(true)
  maybeShowStageHint(stage)
  try { addMsg('ai', await session.kickoff()) } catch (e) { addMsg('ai', safeError(e)) }
  busy(false); input.focus()

  const submit = async () => {
    const text = input.value.trim()
    if (!text || send.disabled) return
    input.value = ''
    addMsg('user', text)
    busy(true)
    try { addMsg('ai', await session.say(text)) } catch (e) { addMsg('ai', safeError(e)) }
    busy(false); input.focus()
  }
  send.addEventListener('click', submit)
  // isComposing/229: picking an IME candidate (Japanese, Chinese — both
  // supported practice languages) confirms with Enter; that must not send a
  // half-composed line.
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !e.isComposing && e.keyCode !== 229) submit() })

  function finish () {
    // Same repeat-click guard as runVoiceRound.endRound: the End round button
    // stays visible over the review panel, so without this a second click (or
    // a stray Escape) re-runs showReview() and fires a second Gemini review call.
    if (finished) return
    finished = true
    const endBtn = stage.querySelector('.nb-live-end')
    if (endBtn) endBtn.style.display = 'none'
    const durationSeconds = roundStart ? Math.round((Date.now() - roundStart) / 1000) : 0
    const stepsCompleted = totalSteps ? stage.querySelectorAll('.nb-live-step.is-done').length : 0
    const criteriaCompleted = totalCriteria ? stage.querySelectorAll('.nb-live-criterion.is-done').length : 0
    gaEvent('live_session_complete', Object.assign({ transport: 'text' }, ctx.gaCtx))
    showReview(stage, brief, session.transcript.slice(), key, ctx, { transport: 'text', durationSeconds, stepsCompleted, totalSteps, criteriaCompleted, totalCriteria })
  }
}

// ── brief fetch + card wiring ───────────────────────────────────────────────

const briefCache = {}
// `overrides` (all optional: difficulty/language/learnerName/duration)
// re-fetches a brief tailored to the request; any of them bypasses the cache,
// same reasoning as difficulty alone used to ("beat your last round" replay
// must actually get harder, and a learner's name/language/round-length pick
// must actually apply).
function fetchBrief (blockId, overrides) {
  const ov = overrides || {}
  const hasOverride = !!(ov.difficulty || ov.language || ov.learnerName || ov.duration)
  if (!hasOverride && briefCache[blockId]) return briefCache[blockId]
  const params = new URLSearchParams()
  if (ov.difficulty) params.set('difficulty', ov.difficulty)
  if (ov.language) params.set('language', ov.language)
  if (ov.learnerName) params.set('learner_name', ov.learnerName)
  if (ov.duration) params.set('duration', ov.duration)
  const qs = params.toString()
  const url = `/api/v1/scenarios/${blockId}/brief${qs ? '?' + qs : ''}`
  const promise = fetch(url, { credentials: 'include', headers: { Accept: 'application/json' } })
    .then((r) => { if (!r.ok) throw new Error('brief unavailable'); return r.json() })
  if (!hasOverride) {
    briefCache[blockId] = promise
    // Never cache a rejection: one transient network failure would otherwise
    // kill Scene notes and every default-prefs start until a page reload.
    promise.catch(() => { if (briefCache[blockId] === promise) delete briefCache[blockId] })
  }
  return promise
}

// The brief a card's modals (settings/welcome language list) should read
// from. A custom-interview card carries its generated brief (or, before any
// generation, a pseudo-brief assembled from the builder's embedded config)
// instead of a fetchable block id.
function cardBrief (card) {
  if (card && card._ciBrief) return Promise.resolve(card._ciBrief)
  if (card && card._settingsBrief) return Promise.resolve(card._settingsBrief)
  return fetchBrief(card.dataset.blockId)
}

// ── custom live interview builder (docs/live-practice-hub-spec.md L5a) ──────
// Generation runs here, on the visitor's own key (same zero-server-cost
// architecture as rounds); Rails only stores the typed prompts, clamps the
// generated options, and assembles the brief server-side.

const CI_HISTORY_KEY = 'ab_ci_history'

// Gemini structured-output schema for the generation call. Transport config,
// not prompt content: the meta prompt itself lives server-side
// (CustomInterviewPrompt) and arrives via the builder page's #ci-config.
const CI_RESPONSE_SCHEMA = {
  type: 'OBJECT',
  properties: {
    setting: { type: 'STRING' },
    persona: { type: 'STRING' },
    learner_role: { type: 'STRING' },
    goal: { type: 'STRING' },
    first_line: { type: 'STRING' },
    questions: {
      type: 'ARRAY',
      items: {
        type: 'OBJECT',
        properties: {
          question: { type: 'STRING' },
          model_answer: { type: 'STRING' },
          follow_up: { type: 'STRING' }
        },
        required: ['question', 'model_answer']
      }
    },
    steps: { type: 'ARRAY', items: { type: 'STRING' } },
    success_criteria: { type: 'ARRAY', items: { type: 'STRING' } }
  },
  required: ['setting', 'persona', 'learner_role', 'goal', 'first_line',
    'questions', 'steps', 'success_criteria']
}

function readCiHistory () {
  try { return JSON.parse(localStorage.getItem(CI_HISTORY_KEY) || '[]') || [] } catch (e) { return [] }
}

function storeCiHistory (list) {
  try { localStorage.setItem(CI_HISTORY_KEY, JSON.stringify(list.slice(0, 5))) } catch (e) { /* storage blocked */ }
}

function pushCiHistory (entry) {
  const list = readCiHistory().filter(e => e && e.id !== entry.id)
  list.unshift(entry)
  storeCiHistory(list)
}

// Mirror the server's post-round counters into the local history entry (the
// custom-interview stand-in for recordLocal/paintBadge).
function updateCiHistoryStats (id, best, rounds) {
  const list = readCiHistory()
  const hit = list.find(e => e && String(e.id) === String(id))
  if (!hit) return
  if (typeof best === 'number') hit.best = best
  if (typeof rounds === 'number') hit.rounds = rounds
  storeCiHistory(list)
}

// Walk the text-model ladder generating the scenario JSON on the visitor's
// key. Placeholder substitution is split/join on purpose: user text must
// never reach a RegExp. Throws with err.reason in {quota, parse, network,
// error} for the GA funnel.
async function generateScenario (cfg, key, topic, outcome, prefs, difficulty, source) {
  const langs = Array.isArray(cfg.languages) ? cfg.languages : []
  const langCode = prefs.language || 'en-US'
  const langLabel = (langs.find(l => l.code === langCode) || {}).label || 'English'
  const prompt = String(cfg.meta_prompt || '')
    .split('{{TOPIC}}').join(topic)
    .split('{{OUTCOME}}').join(outcome)
    .split('{{LANGUAGE_NAME}}').join(langLabel)
    .split('{{DIFFICULTY}}').join(difficulty)
    .split('{{SOURCE_MATERIAL}}').join(source || '')
  let lastReason = 'error'
  for (const model of usableModels(cfg.review_models)) {
    // Two attempts per rung: unparseable JSON gets one retry on the same
    // model before falling to the next; quota/access failures fall straight
    // through (and memoize the skip).
    for (let attempt = 0; attempt < 2; attempt++) {
      let res
      try {
        res = await fetch(`${GEMINI_HOST}/v1beta/models/${model}:generateContent`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-goog-api-key': key },
          body: JSON.stringify({
            contents: [{ role: 'user', parts: [{ text: prompt }] }],
            generationConfig: { responseMimeType: 'application/json', responseSchema: CI_RESPONSE_SCHEMA }
          })
        })
      } catch (e) { lastReason = 'network'; break }
      if (res.status === 429 || res.status === 403) { skipModel(model); lastReason = 'quota'; break }
      if (!res.ok) { lastReason = 'error'; break }
      try {
        const data = await res.json()
        const raw = data.candidates && data.candidates[0] && data.candidates[0].content && data.candidates[0].content.parts[0].text
        const parsed = JSON.parse(raw)
        if (parsed && Array.isArray(parsed.questions) && parsed.questions.length) return { options: parsed, model }
        lastReason = 'parse'
      } catch (e) { lastReason = 'parse' }
    }
  }
  throw Object.assign(new Error('generation failed'), { reason: lastReason })
}

// The ?quiz= seed's MCQs as the {{SOURCE_MATERIAL}} prompt block: question +
// correct answer per line, compact enough for the free-tier context window.
function ciSourceMaterial (seed) {
  return seed.questions.map((q, i) =>
    `Q${i + 1}: ${q.q}` + (q.a ? `\nCorrect answer: ${q.a}` : '')
  ).join('\n')
}

// Re-assemble a built interview's brief from its stored options (history
// replay, and "practise again (harder)"). The server skips both the budget
// and the insert on this path; it just re-clamps and re-briefs.
async function ciReplay (entry, difficulty) {
  const prefs = readPrefs()
  const res = await postJSON('/api/v1/custom_interviews', {
    replay_of: entry.id,
    options: entry.options,
    difficulty: difficulty || entry.difficulty,
    language: prefs.language || undefined,
    learner_name: prefs.name || undefined,
    duration: prefs.duration || undefined,
    anon_token: anonToken()
  })
  const d = await res.json().catch(() => null)
  if (!res.ok || !d || !d.success) throw new Error((d && d.info) || 'replay failed')
  return d.brief
}

function initCustomBuilder () {
  const root = document.querySelector('[data-ci-builder]')
  if (!root) return
  let cfg
  try { cfg = JSON.parse(document.getElementById('ci-config').textContent) } catch (e) { return }
  const form = root.querySelector('[data-ci-form]')
  const topicInput = root.querySelector('#ci-topic')
  const outcomeInput = root.querySelector('#ci-outcome')
  const difficultySelect = root.querySelector('#ci-difficulty')
  const statusEl = root.querySelector('[data-ci-status]')
  const card = root.querySelector('[data-ci-card]')

  // Lets the settings gear (and the welcome step) offer the full language
  // list before any interview exists to fetch a brief from.
  card._settingsBrief = {
    supported_languages: cfg.languages,
    validate_model: cfg.review_models[cfg.review_models.length - 1],
    review_model: cfg.review_models[0],
    language: ''
  }

  gaEvent('ci_builder_view')

  const setStatus = (text, bad) => {
    statusEl.textContent = text || ''
    statusEl.className = 'nb-ci-status' + (bad ? ' is-bad' : '')
  }

  root.querySelector('[data-ci-settings]').addEventListener('click', () => openSettingsModal(card))

  // Question-page funnel: ?quiz= seeds cfg.seed with that quiz's real MCQs
  // (two quizzes can share a name; only the questions make the interview
  // specific), ?topic= is the bare fallback when the seed comes back empty.
  const seed = (cfg.seed && Array.isArray(cfg.seed.questions) && cfg.seed.questions.length) ? cfg.seed : null
  const preTopic = (seed && seed.topic) || new URLSearchParams(location.search).get('topic')
  if (preTopic && !topicInput.value) {
    topicInput.value = preTopic.slice(0, 300)
    setStatus(seed
      ? `Loaded ${seed.questions.length} questions from the quiz you were reading. You will practise the same concepts by voice: an AI interviewer asks, you answer out loud. Add what you want to improve, then build.`
      : 'Topic filled in from the quiz you were reading. You will practise those MCQs by voice: an AI interviewer asks, you answer out loud. Add what you want to improve, then build.')
    gaEvent('ci_topic_prefill', seed ? { quiz_id: seed.quiz_id } : undefined)
  }

  function showCard (id, brief, entry) {
    card._ciBrief = brief
    card._ciEntry = entry
    card.dataset.ciId = id
    // A per-interview pseudo block id keys the localStorage last-review
    // mirror ("Last review" chip) without colliding across interviews.
    card.dataset.blockId = 'ci-' + id
    card.dataset.difficulty = brief.difficulty || 'intermediate'
    card.querySelector('[data-ci-card-title]').textContent = brief.title || 'Custom interview'
    card.querySelector('[data-ci-card-setting]').textContent = (brief.learner_brief && brief.learner_brief.setting) || ''
    card.hidden = false
    paintReviewLink(card)
    card.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  function renderCiHistory () {
    const wrap = root.querySelector('[data-ci-history-wrap]')
    const list = root.querySelector('[data-ci-history]')
    const entries = readCiHistory()
    wrap.hidden = !entries.length
    list.replaceChildren()
    entries.forEach((entry) => {
      const li = el('li')
      const btn = el('button')
      btn.type = 'button'
      const meta = []
      if (entry.difficulty) meta.push(entry.difficulty)
      if (typeof entry.best === 'number') meta.push(`best ${entry.best}/5`)
      if (entry.rounds) meta.push(`${entry.rounds} round${entry.rounds > 1 ? 's' : ''}`)
      btn.append(
        el('span', 'nb-ci-history-topic', entry.topic),
        el('span', 'nb-ci-history-meta', [entry.outcome, ...meta].filter(Boolean).join(' · '))
      )
      btn.addEventListener('click', async () => {
        setStatus('Loading your interview…')
        try {
          const brief = await ciReplay(entry)
          setStatus('')
          showCard(entry.id, brief, entry)
        } catch (e) {
          setStatus('Could not load that interview (it may belong to another browser). Build it again.', true)
        }
      })
      li.appendChild(btn)
      list.appendChild(li)
    })
  }
  renderCiHistory()

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    gaEvent('ci_generate_click')
    const topic = topicInput.value.trim()
    const outcome = outcomeInput.value.trim()
    if (!topic || !outcome) { setStatus('Fill in both fields to build your interview.', true); return }
    if (topic.length > 300 || outcome.length > 300) { setStatus('Keep each field under 300 characters.', true); return }
    // BYOK required to build (the builder doubles as key onboarding): no key
    // yet -> open settings, they save one and click Build again.
    if (!readKey()) {
      setStatus('Add your free Gemini key first (it takes ~30 seconds), then click Build again.')
      openSettingsModal(card)
      return
    }
    const buildBtn = form.querySelector('[data-ci-build]')
    buildBtn.disabled = true
    setStatus('Building your interview… this takes 10-20 seconds.')
    const prefs = readPrefs()
    const difficulty = difficultySelect.value
    // The MCQ seed only grounds the build while the topic is still the seeded
    // quiz; a visitor who retypes the topic wants a different interview.
    const seedActive = seed && topic === (seed.topic || '').slice(0, 300)
    try {
      const gen = await generateScenario(cfg, readKey(), topic, outcome, prefs, difficulty, seedActive ? ciSourceMaterial(seed) : '')
      const res = await postJSON('/api/v1/custom_interviews', {
        topic: topic,
        outcome: outcome,
        model: gen.model,
        language: prefs.language || undefined,
        difficulty: difficulty,
        options: gen.options,
        learner_name: prefs.name || undefined,
        duration: prefs.duration || undefined,
        anon_token: anonToken()
      })
      const d = await res.json().catch(() => null)
      if (res.status === 429) {
        setStatus((d && d.info) || 'Daily build limit reached; replay one from your history instead.', true)
        return
      }
      if (!res.ok || !d || !d.success) {
        setStatus((d && d.info) || 'Could not save the interview, try again.', true)
        return
      }
      gaEvent('ci_generated', { model: gen.model })
      const entry = { id: d.id, topic: topic, outcome: outcome, options: gen.options, model: gen.model, difficulty: difficulty, created: Date.now() }
      pushCiHistory(entry)
      renderCiHistory()
      setStatus('')
      showCard(d.id, d.brief, entry)
    } catch (err) {
      const reason = (err && err.reason) || 'error'
      gaEvent('ci_generate_failed', { reason: reason })
      setStatus(reason === 'quota'
        ? 'Your key is out of free requests for today. The quota resets at midnight Pacific time.'
        : 'Generation failed, try again.', true)
    } finally {
      buildBtn.disabled = false
    }
  })
}

// ── hub filters (docs/live-practice-hub-spec.md L4a) ────────────────────────
function initHubFilters () {
  const hub = document.querySelector('[data-live-hub]')
  if (!hub) return
  gaEvent('live_hub_view')
  const buttons = hub.querySelectorAll('[data-practice-filter]')
  if (!buttons.length) return
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const flavor = btn.dataset.practiceFilter
      buttons.forEach(b => b.classList.toggle('is-active', b === btn))
      // Two-level hub: the filter toggles topic cards, not scenario cards.
      hub.querySelectorAll('li[data-flavor]').forEach((li) => {
        li.hidden = flavor !== 'all' && li.dataset.flavor !== flavor
      })
      gaEvent('live_hub_filter', { flavor: flavor })
    })
  })
}

// Returning users (a Gemini key already saved on this device) have been
// through the how-to and key guides, so fold them down to their headings and
// let the topic directory surface. The server always renders both <details>
// open, so crawlers, first-timers, and no-JS visitors see the full guides;
// the summary heading stays clickable to reopen. Arriving at #get-a-key
// (linked from the interview builder) keeps the key guide open regardless.
function initHubFolds () {
  const hub = document.querySelector('[data-live-hub]')
  if (!hub || !readKey()) return
  hub.querySelectorAll('details[data-hub-fold]').forEach((fold) => {
    const section = fold.closest('section')
    if (section && section.id && window.location.hash === '#' + section.id) return
    fold.open = false
    const summary = fold.querySelector('summary')
    if (summary && !summary.querySelector('.nb-hub-fold-hint')) {
      summary.appendChild(el('span', 'nb-hub-fold-hint', '✓ ' + fold.dataset.hubFold))
    }
  })
}

// Click-to-enlarge for the hub's screenshots (how-strip + key guide): the
// thumbnails are too small to actually read, so a click opens the full-size
// asset through the shared modalOverlay scaffolding (Escape, outside-click,
// and focus-restore behave like every other dialog here).
function initHubZoom () {
  const hub = document.querySelector('[data-live-hub]')
  if (!hub) return
  const SELECTOR = '.nb-hub-how img, .nb-hub-key-steps img'
  // Slider group = the strip the clicked image sits in (the 4 round shots, or
  // the key-guide shots), so prev/next never jumps between the two guides.
  const groupOf = (img) => {
    const wrap = img.closest('.nb-hub-how, .nb-hub-key-steps')
    return wrap ? Array.from(wrap.querySelectorAll('img')) : [img]
  }
  const openZoom = (img) => {
    const group = groupOf(img)
    let index = Math.max(0, group.indexOf(img))
    const panel = el('figure', 'nb-hub-lightbox')
    panel.setAttribute('aria-label', 'Enlarged screenshot')
    const closeBtn = el('button', 'nb-hub-lightbox-close')
    closeBtn.type = 'button'
    closeBtn.setAttribute('aria-label', 'Close enlarged screenshot')
    closeBtn.textContent = '×'
    const big = el('img')
    const caption = el('figcaption')
    caption.setAttribute('aria-live', 'polite')
    const paint = () => {
      const cur = group[index]
      big.src = cur.currentSrc || cur.src
      big.alt = cur.alt
      caption.textContent = group.length > 1 ? `${index + 1}/${group.length} · ${cur.alt}` : cur.alt
    }
    const step = (delta) => { index = (index + delta + group.length) % group.length; paint() }
    panel.append(closeBtn, big, caption)
    if (group.length > 1) {
      const prev = el('button', 'nb-hub-lightbox-nav is-prev', '←')
      const next = el('button', 'nb-hub-lightbox-nav is-next', '→')
      prev.type = next.type = 'button'
      prev.setAttribute('aria-label', 'Previous screenshot')
      next.setAttribute('aria-label', 'Next screenshot')
      prev.addEventListener('click', () => step(-1))
      next.addEventListener('click', () => step(1))
      panel.append(prev, next)
    }
    paint()
    const modal = modalOverlay(panel)
    modal.overlay.addEventListener('keydown', (e) => {
      if (group.length < 2) return
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1) }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1) }
    })
    closeBtn.addEventListener('click', () => modal.close())
    big.addEventListener('click', () => modal.close())
  }
  // Images aren't natively focusable; give them button semantics so the
  // lightbox is reachable by keyboard too.
  hub.querySelectorAll(SELECTOR).forEach((img) => {
    img.setAttribute('tabindex', '0')
    img.setAttribute('role', 'button')
  })
  hub.addEventListener('click', (e) => {
    const img = e.target.closest(SELECTOR)
    if (img) openZoom(img)
  })
  hub.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    const img = e.target.closest && e.target.closest(SELECTOR)
    if (!img) return
    e.preventDefault()
    openZoom(img)
  })
}

// Transient inline error on the card (e.g. the brief fetch failed): the start
// buttons must never be a silent dead click.
function flashCardError (card, message) {
  let note = card.querySelector('.nb-scenario-error')
  if (!note) {
    note = el('p', 'nb-scenario-error')
    note.setAttribute('role', 'alert')
    const actions = card.querySelector('.nb-scenario-actions')
    if (actions) actions.insertAdjacentElement('afterend', note)
    else card.appendChild(note)
  }
  note.textContent = message
  clearTimeout(note._hideTimer)
  note._hideTimer = setTimeout(() => note.remove(), 6000)
}

const DIFFICULTY_LADDER = ['beginner', 'intermediate', 'advanced']
function escalate (difficulty) {
  const i = DIFFICULTY_LADDER.indexOf(difficulty)
  return DIFFICULTY_LADDER[Math.min(i + 1, DIFFICULTY_LADDER.length - 1)] || 'intermediate'
}

function gaContext (card) {
  return {
    page_id: card.dataset.pageId,
    block_id: card.dataset.blockId,
    flavor: card.dataset.flavor,
    difficulty: card.dataset.difficulty,
    tier: 'byok'
  }
}

async function start (card, transport) {
  const gaCtx = gaContext(card)
  gaEvent('live_start_click', Object.assign({ transport }, gaCtx))
  // Very first round on this device: one welcome step (what a round looks
  // like + name/language) before anything else. Backing out abandons the
  // start click entirely; the flag is only set once they continue.
  if (!flagSeen(SETUP_KEY)) {
    gaEvent('live_onboard_shown', gaCtx)
    const proceed = await firstRunSetup(card, gaCtx)
    if (!proceed) return
  }
  const prefs = readPrefs()
  let brief
  if (card._ciBrief) {
    // Custom interview: the brief was assembled at build/replay time (with
    // prefs already applied server-side); there is no block to fetch.
    brief = card._ciBrief
    gaEvent('ci_round_start', { transport: transport })
  } else {
    try {
      brief = await fetchBrief(card.dataset.blockId, { language: prefs.language, learnerName: prefs.name, duration: prefs.duration })
    } catch (e) {
      flashCardError(card, 'Could not load this scenario. Check your connection and try again.')
      return
    }
  }

  const ctx = { gaCtx, card, transport, targetPhrases: brief.target_phrases, difficulty: brief.difficulty || card.dataset.difficulty, _replay: null }
  // Replay escalates difficulty (spec §Progression: "beat your last round" =
  // same scene, persona faster / fewer retries / more complications, which is
  // just the difficulty param). Re-fetches an escalated brief, keeps transport
  // and re-reads prefs fresh in case settings changed since the round started.
  ctx._replay = async () => {
    ctx.difficulty = escalate(ctx.difficulty)
    const p = readPrefs()
    let next
    if (card.dataset.ciId && card._ciEntry) {
      // Custom interview: replay re-briefs from the stored options (no new
      // row, no budget spend) at the escalated difficulty.
      try { next = await ciReplay(card._ciEntry, ctx.difficulty) } catch (e) { return }
      card._ciBrief = next
    } else {
      try { next = await fetchBrief(card.dataset.blockId, { difficulty: ctx.difficulty, language: p.language, learnerName: p.name, duration: p.duration }) } catch (e) { return }
    }
    ctx.targetPhrases = next.target_phrases
    return ctx.transport === 'text' ? runTextRound(card, next, ctx) : runVoiceRound(card, next, ctx)
  }
  if (transport === 'text') { gaEvent('live_text_mode_start', gaCtx); return runTextRound(card, brief, ctx) }
  return runVoiceRound(card, brief, ctx)
}

function init () {
  initHubFilters()
  initHubFolds()
  initHubZoom()
  initCustomBuilder()
  const cards = document.querySelectorAll('.nb-scenario')
  if (!cards.length) return

  // Paint best-score/rounds badges + the "Last review" chip from the local
  // mirror, and (if signed in) hand any guest rounds to the account — same
  // shape as reader/progress.js.
  cards.forEach((c) => { paintBadge(c); paintReviewLink(c); paintHowItWorks(c) })
  claimRounds()

  // Fire live_card_view once when a card scrolls into view.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          gaEvent(entry.target.dataset.gaView || 'live_card_view', gaContext(entry.target))
          io.unobserve(entry.target)
        }
      })
    }, { threshold: 0.4 })
    cards.forEach(c => io.observe(c))
  }

  document.addEventListener('click', (event) => {
    const voiceBtn = event.target.closest('[data-live-start]')
    if (voiceBtn) { const card = voiceBtn.closest('.nb-scenario'); if (card) start(card, 'voice'); return }
    const textBtn = event.target.closest('[data-live-start-text]')
    if (textBtn) { const card = textBtn.closest('.nb-scenario'); if (card) start(card, 'text'); return }
    const settingsBtn = event.target.closest('[data-live-settings]')
    if (settingsBtn) { const card = settingsBtn.closest('.nb-scenario'); if (card) openSettingsModal(card); return }
    const briefBtn = event.target.closest('[data-live-brief]')
    if (briefBtn) {
      const card = briefBtn.closest('.nb-scenario')
      if (card) fetchBrief(card.dataset.blockId).then(openBriefModal).catch(() => openBriefModal(null))
    }
  })

  // Esc closes the topmost settings/scene-notes modal first; only with no
  // modal open does it end an in-progress round. (It used to end the whole
  // round straight through an open modal.)
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return
    const overlays = document.querySelectorAll('.nb-key-modal-overlay')
    if (overlays.length) {
      const top = overlays[overlays.length - 1]
      if (top._close) top._close(); else top.remove() // _close also restores focus
      return
    }
    const endBtn = document.querySelector('.nb-live-stage .nb-live-end')
    if (endBtn) endBtn.click()
  })
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
else init()
